import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LogIn, LogOut, RefreshCw, Filter, X, ChevronDown, ChevronRight, ChevronLeft, Package, DollarSign, AlertCircle, Menu, Plus, Trash2, Search, FileText, CheckSquare, Square, Edit3, BarChart3, TrendingUp, TrendingDown, Globe, Table2, ClipboardList, Copy, Printer, Eye, BookOpen, Settings, ShoppingCart, Download, Upload, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

// ============================================
// CONFIG
// ============================================
const API_URL = process.env.REACT_APP_API_URL || '';

// ============================================
// API Helper
// ============================================
async function api(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

  if (res.status === 401 || res.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    window.location.reload();
    throw new Error('Sesión expirada');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Error ${res.status}`);
  }

  return res.json();
}

// ============================================
// Formateo de números
// ============================================
const fmtStock = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });
const fmtNum = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });

// ============================================
// Utility functions
// ============================================
function parseDiamNum(val) {
  if (!val) return null;
  const s = String(val).trim();
  const m1 = s.match(/^(\d+)\s+(\d+)\/(\d+)/);
  if (m1) return parseInt(m1[1]) + parseInt(m1[2]) / parseInt(m1[3]);
  const m2 = s.match(/^(\d+)\/(\d+)/);
  if (m2) return parseInt(m2[1]) / parseInt(m2[2]);
  const m3 = s.match(/^(\d+(?:[.,]\d+)?)/);
  if (m3) return parseFloat(m3[1].replace(',', '.'));
  return null;
}

function displayDiam(raw) {
  if (!raw) return raw;
  const s = String(raw).trim();
  const tbgMatch = s.match(/^TUBING\s+(\d+)\/(\d+)/i);
  if (tbgMatch) {
    const map = {'2/7': '2 7/8', '3/5': '3 1/2', '2/3': '2 3/8', '4/5': '4 1/2'};
    return `TBG ${map[`${tbgMatch[1]}/${tbgMatch[2]}`] || `${tbgMatch[1]}/${tbgMatch[2]}`}"`;
  }
  const varMatch = s.match(/^VARILLA\s+([\d\/]+)/i);
  if (varMatch) return `VAR ${varMatch[1]}"`;
  const csgMatch = s.match(/^CASING\s+([\d\s\/]+)/i);
  if (csgMatch) return `CSG ${csgMatch[1].trim()}"`;
  if (/^\d/.test(s) && !s.includes('"')) return `${s}"`;
  return s;
}

function groupDiamByDisplay(rawValues) {
  const groups = {};
  rawValues.forEach(raw => {
    const label = displayDiam(raw);
    if (!groups[label]) groups[label] = { label, raws: [] };
    groups[label].raws.push(raw);
  });
  return Object.values(groups);
}

function useCountUp(target, duration = 800) {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(0);
  useEffect(() => {
    const start = prevTarget.current;
    prevTarget.current = target;
    if (target === 0 && start === 0) return;
    const startTime = performance.now();
    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(start + (target - start) * easeOutCubic(progress)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);
  return value;
}

// ============================================
// FilterSection (sidebar filter component)
// ============================================
function FilterSection({ title, color = 'blue', options, selected, onChange, displayFn, grid = false }) {
  const [open, setOpen] = useState(true);
  const activeCount = selected.length;
  const colorMap = {
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-400',
    violet: 'bg-violet-100 text-violet-700 border-violet-400',
    amber: 'bg-amber-100 text-amber-700 border-amber-400',
    teal: 'bg-teal-100 text-teal-700 border-teal-400',
    sky: 'bg-sky-100 text-sky-700 border-sky-400',
    blue: 'bg-blue-100 text-blue-700 border-blue-400',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-400',
  };
  const activeStyle = colorMap[color] || colorMap.blue;
  return (
    <div className="mb-2">
      <div className="flex items-center gap-1 py-1 cursor-pointer select-none" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={10} className="text-gray-400" /> : <ChevronRight size={10} className="text-gray-400" />}
        <span className={`text-[11px] uppercase tracking-widest font-bold text-${color}-600`}>{title}</span>
        {activeCount > 0 && (
          <>
            <span className={`text-[10px] ${activeStyle} px-1.5 py-0.5 rounded-full font-bold leading-none`}>{activeCount}</span>
            <button onClick={e => { e.stopPropagation(); onChange([]); }} className="text-red-400 hover:text-red-600 ml-1"><X size={10} /></button>
          </>
        )}
      </div>
      {open && (
        grid ? (
          <div className="grid grid-cols-2 gap-1 mt-0.5">
            {options.map(opt => {
              const active = selected.includes(opt);
              return <button key={opt} onClick={() => active ? onChange(selected.filter(v => v !== opt)) : onChange([...selected, opt])}
                className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? `${activeStyle} shadow-sm font-medium` : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 shadow-sm'}`}>{displayFn ? displayFn(opt) : opt}</button>;
            })}
          </div>
        ) : (
          <div className="max-h-32 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {options.map(opt => (
              <label key={opt} className="flex items-center gap-1.5 py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => {
                  selected.includes(opt) ? onChange(selected.filter(v => v !== opt)) : onChange([...selected, opt]);
                }} className="w-3.5 h-3.5 rounded accent-blue-500 flex-shrink-0" />
                <span className="text-xs text-gray-600 truncate">{displayFn ? displayFn(opt) : opt}</span>
              </label>
            ))}
            {options.length === 0 && <span className="text-[10px] text-gray-400 px-1">Sin opciones</span>}
          </div>
        )
      )}
    </div>
  );
}

// ============================================
// LOGIN PAGE
// ============================================
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api('/api/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('username', data.username);
      onLogin(data.username);
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
      <div className="rounded-2xl shadow-2xl p-10 w-full max-w-sm" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="text-center mb-8">
          <div className="rounded-xl p-3 inline-block mb-4" style={{ color: '#4dabf7' }}>
            <Package size={48} />
          </div>
          <h1 className="text-xl font-bold text-white tracking-widest" style={{ fontFamily: "'Orbitron', sans-serif" }}>COMERCIAL</h1>
          <p className="text-sm mt-1" style={{ color: '#a0aec0' }}>Cuenca del Sur</p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" style={{ color: '#a0aec0' }}>Usuario</label>
            <input
              type="text"
              placeholder="Ingrese su usuario"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full text-white rounded-lg px-4 py-3 focus:outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              onFocus={e => { e.target.style.borderColor = '#4dabf7'; e.target.style.boxShadow = '0 0 0 3px rgba(77,171,247,0.2)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" style={{ color: '#a0aec0' }}>Password</label>
            <input
              type="password"
              placeholder="Ingrese su password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full text-white rounded-lg px-4 py-3 focus:outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              onFocus={e => { e.target.style.borderColor = '#4dabf7'; e.target.style.boxShadow = '0 0 0 3px rgba(77,171,247,0.2)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          {error && (
            <div className="text-sm p-3 rounded-lg flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full text-white py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all font-semibold mt-1 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #4dabf7 0%, #228be6 100%)' }}
          >
            <LogIn size={16} />
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================
// MULTI-SELECT DROPDOWN
// ============================================
function MultiSelect({ label, options, selected, onChange, icon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const displayText = selected.length === 0
    ? label
    : selected.length === 1
      ? selected[0]
      : `${selected.length} seleccionados`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm whitespace-nowrap transition-colors ${
          selected.length > 0
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
        }`}
      >
        {icon}
        <span className="max-w-[140px] truncate">{displayText}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 bg-gray-100 border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto min-w-[180px]">
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border-b border-gray-300"
            >
              Limpiar
            </button>
          )}
          {options.map(opt => (
            <label
              key={opt}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-200 cursor-pointer text-sm text-gray-700"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="rounded accent-blue-500"
              />
              <span className="truncate">{opt}</span>
            </label>
          ))}
          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">Sin opciones</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// HELPERS DE FORMATO
// ============================================
function formatCurrency(value, moneda) {
  if (value == null) return null;
  if (moneda === 'USD') {
    return `USD ${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${Math.round(value).toLocaleString('es-AR')}`;
}

// ============================================
// STOCK TABLE (con filas desplegables por depósito)
// ============================================
const STOCK_COLUMNS = [
  { key: 'articulo', label: 'ARTICULO', align: 'left', type: 'text', sticky: true, width: 'w-[35%]' },
  { key: 'um', label: 'UM', align: 'center', type: 'text', width: 'w-[6%]' },
  { key: 'en_stock', label: 'EN STOCK', align: 'right', type: 'number', width: 'w-[10%]' },
  { key: 'a_entregar', label: 'A ENTREGAR', align: 'right', type: 'number', width: 'w-[10%]' },
  { key: 'stock_neto_np', label: 'STOCK NETO', align: 'right', type: 'number', width: 'w-[10%]' },
  { key: 'precio_lista', label: 'P$ LISTA', align: 'right', type: 'price', width: 'w-[13%]' },
  { key: 'precio_minimo', label: 'P$ MÍNIMO', align: 'right', type: 'price', width: 'w-[13%]' },
];

function parsePriceForSort(val) {
  if (val == null) return -Infinity;
  const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? -Infinity : n;
}

function StockTable({ data, loading, rubroFilter = [] }) {
  const [expanded, setExpanded] = useState(new Set());
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const toggleExpand = (codigo) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!data || !sortCol) return data;
    const col = STOCK_COLUMNS.find(c => c.key === sortCol);
    if (!col) return data;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      if (col.type === 'number') {
        return ((a[sortCol] || 0) - (b[sortCol] || 0)) * dir;
      }
      if (col.type === 'price') {
        return (parsePriceForSort(a[sortCol]) - parsePriceForSort(b[sortCol])) * dir;
      }
      const va = String(a[sortCol] || ''), vb = String(b[sortCol] || '');
      return va.localeCompare(vb, 'es') * dir;
    });
  }, [data, sortCol, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={24} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-gray-400">
        <Package size={28} />
        <p className="mt-2 text-xs">Sin datos para los filtros seleccionados</p>
      </div>
    );
  }

  const priceTooltip = (row, field) => {
    if (!row.lp_numero) return '';
    const lp = `LP #${row.lp_numero} - ${row.lp_tipo_producto || ''}`;
    if (field === 'precio_lista') {
      if (row.kg_mt_usado) {
        const fuente = row.kg_mt_fuente === 'norma' ? 'Norma' : 'Sheet';
        return `${lp}\nFórmula: ${formatCurrency(row.precio_lista_unit, row.moneda)}/kg × ${fmtStock.format(row.kg_mt_usado)} kg/mt\nFuente kg/mt: ${fuente}`;
      }
      return `${lp}\nPrecio directo`;
    }
    if (field === 'precio_minimo') {
      return `${lp}\nDesc.Max: ${row.descuento_max != null ? Math.round(row.descuento_max * 100) + '%' : '-'}`;
    }
    return '';
  };

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-20">
        <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
          {STOCK_COLUMNS.map(col => (
            <th
              key={col.key}
              onClick={() => handleSort(col.key)}
              className={`text-${col.align} px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-[#003d70] transition-colors whitespace-nowrap ${col.sticky ? 'sticky left-0 bg-[#004a8a] z-10' : ''} ${col.width || ''}`}
            >
              <span className="inline-flex items-center gap-1">
                {col.align === 'right' && sortCol === col.key && (
                  <span className="text-yellow-300 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
                {col.label}
                {col.align !== 'right' && sortCol === col.key && (
                  <span className="text-yellow-300 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((row, i) => {
          const isExpanded = expanded.has(row.codigo);
          const hasMultiDep = row.depositos && row.depositos.length > 1;
          return (
            <React.Fragment key={row.codigo}>
              <tr
                className={`border-b border-gray-200/40 hover:bg-blue-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${hasMultiDep ? 'cursor-pointer' : ''}`}
                onClick={() => hasMultiDep && toggleExpand(row.codigo)}
              >
                <td className="px-3 py-2 font-medium text-gray-700 sticky left-0 bg-inherit z-10 whitespace-nowrap text-[13px]" style={{ boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>
                  <span className="inline-flex items-center gap-1.5">
                    {hasMultiDep ? (
                      isExpanded ? <ChevronDown size={14} className="text-blue-600 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                    ) : (
                      <span className="w-3.5 inline-block flex-shrink-0" />
                    )}
                    {row.articulo}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-gray-500">{row.um || <span className="text-gray-300">-</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtStock.format(row.en_stock)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtStock.format(row.a_entregar)}</td>
                <td className={`px-3 py-2 text-right font-bold tabular-nums ${row.stock_neto_np < 0 ? 'text-red-500 bg-red-50' : 'text-emerald-600 bg-emerald-50'}`}>
                  {fmtStock.format(row.stock_neto_np)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500 whitespace-nowrap" title={priceTooltip(row, 'precio_lista')}>
                  {row.precio_lista != null ? (
                    <span className="inline-flex items-center gap-1">
                      {formatCurrency(row.precio_lista, row.moneda)}
                      {row.um_venta && <span className="text-gray-400 text-[10px]">/{row.um_venta === 'Metro' ? 'mt' : row.um_venta === 'Unidad' ? 'u' : row.um_venta}</span>}
                      {row.kg_mt_fuente === 'norma' && <span className="text-blue-500 text-[9px] font-bold">N</span>}
                    </span>
                  ) : <span className="text-gray-300">-</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500 whitespace-nowrap" title={priceTooltip(row, 'precio_minimo')}>
                  {row.precio_minimo != null ? <>{formatCurrency(row.precio_minimo, row.moneda)}{row.um_venta && <span className="text-gray-400 text-[10px] ml-1">/{row.um_venta === 'Metro' ? 'mt' : row.um_venta === 'Unidad' ? 'u' : row.um_venta}</span>}</> : <span className="text-gray-300">-</span>}
                </td>
              </tr>
              {isExpanded && (row.depositos || []).filter(dep => rubroFilter.length === 0 || rubroFilter.includes(dep.rubro)).map((dep, j) => (
                <tr
                  key={`${row.codigo}-dep-${j}`}
                  className="border-b border-blue-100 bg-blue-50"
                >
                  <td className="pl-6 pr-3 py-1.5 text-blue-600 text-xs sticky left-0 bg-blue-50 z-10 whitespace-nowrap border-l-2 border-blue-300" style={{ boxShadow: '2px 0 4px rgba(0,0,0,0.15)' }}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500/60 flex-shrink-0" />
                      {dep.nomdeposito}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center text-xs text-blue-600/70">{row.um}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs text-blue-600/80">{fmtStock.format(dep.en_stock)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs text-blue-600/60">{fmtStock.format(dep.a_entregar)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums text-xs font-medium ${dep.stock_neto_np < 0 ? 'text-red-500' : 'text-blue-600/80'}`}>
                    {fmtStock.format(dep.stock_neto_np)}
                  </td>
                  <td></td><td></td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// ============================================
// STOCK PAGE (Session 29 - Left sidebar filters + Excel export)
// ============================================
let _stockCache = null;
function StockPage() {
  const [stockData, setStockData] = useState(_stockCache);
  const [loading, setLoading] = useState(!_stockCache);
  const [error, setError] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [stockSearch, setStockSearch] = useState('');
  const [soloConStock, setSoloConStock] = useState(false);
  const [filters, setFilters] = useState({
    lp_origen: [], condicion: [], industria: [], tipo: [], um: [],
    rubro_deposito: [], norma_asignada: [], norma_sch: [],
    diametro: [], espesor: []
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const data = await api('/api/stock');
      setStockData(data); _stockCache = data;
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!_stockCache) loadData(); }, [loadData]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const cascadeOptions = useMemo(() => {
    if (!stockData?.data) return {};
    const allRows = stockData.data;
    const applyFilters = (excludeKey) => {
      return allRows.filter(row => {
        for (const [k, v] of Object.entries(filters)) {
          if (k === excludeKey || v.length === 0) continue;
          if (k === 'rubro_deposito') {
            if (!(row.rubros_deposito || []).some(rb => v.includes(rb))) return false;
          } else if (!v.includes(row[k])) return false;
        }
        return true;
      });
    };
    const buildOpts = (key, sortFn) => {
      const rows = applyFilters(key);
      const vals = key === 'rubro_deposito'
        ? [...new Set(rows.flatMap(r => r.rubros_deposito || []).filter(Boolean))]
        : [...new Set(rows.map(r => r[key]).filter(Boolean))];
      return sortFn ? vals.sort(sortFn) : vals.sort((a, b) => String(a).localeCompare(String(b), 'es'));
    };
    const sortDiam = (a, b) => { const na = parseDiamNum(a), nb = parseDiamNum(b); if (na !== null && nb !== null) return na - nb; return String(a).localeCompare(String(b), 'es'); };
    const sortEsp = (a, b) => { const na = parseFloat(String(a).replace(',', '.')), nb = parseFloat(String(b).replace(',', '.')); if (!isNaN(na) && !isNaN(nb)) return na - nb; return String(a).localeCompare(String(b), 'es'); };
    const sortSCH = (a, b) => { const na = parseInt(a), nb = parseInt(b); if (!isNaN(na) && !isNaN(nb)) return na - nb; return String(a).localeCompare(String(b), 'es'); };
    return {
      lp_origen: ['NACIONAL', 'IMPORTADO', 'PRENSA', 'USADO'],
      condicion: buildOpts('condicion', (a, b) => { if (a === 'Otros') return 1; if (b === 'Otros') return -1; return a.localeCompare(b, 'es'); }),
      industria: buildOpts('industria'),
      tipo: buildOpts('tipo'),
      um: buildOpts('um'),
      rubro_deposito: buildOpts('rubro_deposito'),
      norma_asignada: buildOpts('norma_asignada'),
      norma_sch: buildOpts('norma_sch', sortSCH),
      diametro: buildOpts('diametro', sortDiam),
      espesor: buildOpts('espesor', sortEsp),
    };
  }, [stockData, filters]);

  // Auto-prune effect
  useEffect(() => {
    if (!cascadeOptions.condicion) return;
    setFilters(prev => {
      const next = { ...prev };
      let changed = false;
      for (const [key, opts] of Object.entries(cascadeOptions)) {
        if (key === 'lp_origen') continue;
        const pruned = (prev[key] || []).filter(v => opts.includes(v));
        if (pruned.length !== (prev[key] || []).length) { next[key] = pruned; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [cascadeOptions]);

  const filteredData = useMemo(() => {
    if (!stockData?.data) return [];
    const hasRubroFilter = (filters.rubro_deposito || []).length > 0;
    return stockData.data.filter(row => {
      if (stockSearch) {
        const q = stockSearch.toLowerCase();
        if (!row.articulo?.toLowerCase().includes(q) && !row.codigo?.includes(stockSearch)) return false;
      }
      if (soloConStock && row.stock_neto_np === 0) return false;
      for (const [k, v] of Object.entries(filters)) {
        if (v.length === 0) continue;
        if (k === 'rubro_deposito') {
          if (!(row.rubros_deposito || []).some(rb => v.includes(rb))) return false;
        } else if (!v.includes(row[k])) return false;
      }
      return true;
    }).map(row => {
      if (!hasRubroFilter) return row;
      const deps = (row.depositos || []).filter(d => filters.rubro_deposito.includes(d.rubro));
      const en_stock = deps.reduce((s, d) => s + d.en_stock, 0);
      const a_entregar = deps.reduce((s, d) => s + d.a_entregar, 0);
      return { ...row, en_stock, a_entregar, stock_neto_np: en_stock - a_entregar };
    });
  }, [stockData, filters, stockSearch, soloConStock]);

  const hasFilters = stockSearch || soloConStock || Object.values(filters).some(f => f.length > 0);
  const clearFilters = () => {
    setStockSearch(''); setSoloConStock(false);
    const reset = {};
    for (const key of Object.keys(filters)) reset[key] = [];
    setFilters(reset);
  };

  // Excel export
  const exportToExcel = () => {
    const rows = filteredData.map(r => ({
      'CÓDIGO': r.codigo, 'ARTÍCULO': r.articulo, 'UM': r.um,
      'EN STOCK': r.en_stock, 'A ENTREGAR': r.a_entregar, 'STOCK NETO': r.stock_neto_np,
      'P$ LISTA': r.precio_lista, 'P$ MÍNIMO': r.precio_minimo,
      'KG/MT': r.kg_mt_usado, 'FUENTE KG/MT': r.kg_mt_fuente || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, `stock_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const SIDEBAR_SECTIONS = [
    { key: 'lp_origen', title: 'Origen', color: 'emerald', fixed: true },
    { key: 'condicion', title: 'Condición', color: 'emerald' },
    { key: 'industria', title: 'Industria', color: 'violet' },
    { key: 'tipo', title: 'Tipo', color: 'amber' },
    { key: 'um', title: 'UM', color: 'teal' },
    { key: 'rubro_deposito', title: 'Depósito', color: 'sky' },
    { key: 'norma_asignada', title: 'Norma', color: 'blue' },
    { key: 'norma_sch', title: 'SCH', color: 'indigo' },
  ];

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 text-gray-800 px-4 py-2 flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Buscar artículo..." value={stockSearch} onChange={e => setStockSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm bg-gray-100 border border-gray-300 text-gray-800 placeholder-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-56" />
          {stockSearch && <button onClick={() => setStockSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"><X size={12} /></button>}
        </div>
        <div className="h-8 w-px bg-gray-100" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{filteredData.length}</span>
          <span className="text-xs text-gray-400">artículos</span>
          {hasFilters && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded">filtrado</span>}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => setSoloConStock(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[11px] whitespace-nowrap transition-colors ${soloConStock ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'}`}>
            <Package size={12} /> Solo con stock
          </button>
          <button onClick={exportToExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border text-[11px] bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 transition-colors">
            <Download size={12} /> Excel
          </button>
          {stockData?.fechaDatos && <span className="text-[10px] text-emerald-600 font-medium">Datos del: {stockData.fechaDatos}</span>}
          {stockData?.fechaActualizacion && <span className="text-[10px] text-gray-400">Últ. sync: {stockData.fechaActualizacion}</span>}
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border border-red-300 rounded-lg transition-colors">
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {error && <div className="px-3 py-2"><div className="bg-red-50 text-red-500 p-2 rounded-lg flex items-center gap-2 text-xs border border-red-200"><AlertCircle size={14} /> {error}</div></div>}

      {/* Main: Sidebar + Table */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar toggle */}
        <button onClick={() => setShowSidebar(prev => !prev)}
          className="flex-shrink-0 w-5 bg-white border-r border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
          title={showSidebar ? 'Ocultar filtros' : 'Mostrar filtros'}>
          {showSidebar ? <ChevronLeft size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
        </button>

        {/* Left Sidebar Filters */}
        {showSidebar && (
          <div className="w-[180px] flex-shrink-0 border-r border-gray-200 overflow-y-auto p-2 bg-white" style={{ scrollbarWidth: 'thin' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">FILTROS</span>
              {hasFilters && <button onClick={clearFilters} className="text-[10px] text-red-500 hover:text-red-700">Limpiar</button>}
            </div>
            {SIDEBAR_SECTIONS.map(sec => (
              <FilterSection key={sec.key} title={sec.title} color={sec.color}
                options={cascadeOptions[sec.key] || []}
                selected={filters[sec.key] || []} onChange={v => updateFilter(sec.key, v)}
                displayFn={sec.key === 'norma_asignada' ? (v) => v || 'Sin norma' : undefined} />
            ))}
            {/* Diámetro */}
            {(() => {
              const opts = cascadeOptions.diametro || [];
              const groups = groupDiamByDisplay(opts);
              const chico = groups.filter(g => { const n = parseDiamNum(g.raws[0]); return n !== null && n < 4; });
              const grande = groups.filter(g => { const n = parseDiamNum(g.raws[0]); return n === null || n >= 4; });
              const toggleGroup = (g) => {
                const allSelected = g.raws.every(r => filters.diametro.includes(r));
                if (allSelected) updateFilter('diametro', filters.diametro.filter(v => !g.raws.includes(v)));
                else updateFilter('diametro', [...new Set([...filters.diametro, ...g.raws])]);
              };
              return (
                <div className="mb-2">
                  <div className="flex items-center gap-1 py-1">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-blue-600">Diámetro</span>
                    {filters.diametro.length > 0 && (
                      <>
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold leading-none">{filters.diametro.length}</span>
                        <button onClick={() => updateFilter('diametro', [])} className="text-red-400 hover:text-red-600 ml-1"><X size={10} /></button>
                      </>
                    )}
                  </div>
                  {chico.length > 0 && (
                    <>
                      <span className="text-[9px] text-gray-400">{'Chico (<4\")'}</span>
                      <div className="grid grid-cols-2 gap-1 mt-0.5 mb-1">
                        {chico.map(g => {
                          const active = g.raws.every(r => filters.diametro.includes(r));
                          return <button key={g.label} onClick={() => toggleGroup(g)}
                            className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 shadow-sm'}`}>{g.label}</button>;
                        })}
                      </div>
                    </>
                  )}
                  {grande.length > 0 && (
                    <>
                      <span className="text-[9px] text-gray-400">{'Grande (≥4\")'}</span>
                      <div className="grid grid-cols-2 gap-1 mt-0.5">
                        {grande.map(g => {
                          const active = g.raws.every(r => filters.diametro.includes(r));
                          return <button key={g.label} onClick={() => toggleGroup(g)}
                            className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 shadow-sm'}`}>{g.label}</button>;
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            {/* Espesor */}
            <FilterSection title="Espesor" color="blue" grid
              options={cascadeOptions.espesor || []} selected={filters.espesor} onChange={v => updateFilter('espesor', v)} />
          </div>
        )}

        {/* Table */}
        <div className="flex-1 min-w-0 p-3 overflow-hidden">
          <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-auto h-full">
            <StockTable data={filteredData} loading={loading} rubroFilter={filters.rubro_deposito} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LISTA DE PRECIOS PAGE (CRUD)
// ============================================
const LP_FIELDS = [
  { key: 'numero', label: '#', align: 'center', width: 'w-12' },
  { key: 'tipo_producto', label: 'Tipo Producto', align: 'left', editable: true },
  { key: 'precio_lista', label: 'P$ Lista', align: 'right', type: 'currency', editable: true },
  { key: 'descuento_max', label: 'Desc.Max', align: 'right', type: 'percent', editable: true },
  { key: 'precio_minimo', label: 'P$ Mínimo', align: 'right', type: 'currency', editable: true },
  { key: 'um_calculo', label: 'UM Cálculo', align: 'center', editable: true },
  { key: 'um_venta', label: 'UM Venta', align: 'center', editable: true },
  { key: 'moneda', label: 'Moneda', align: 'center', editable: true },
  { key: 'origen', label: 'Origen', align: 'center', editable: true, type: 'select', options: ['NACIONAL', 'IMPORTADO', 'PRENSA', 'USADO'] },
  { key: 'articulos_asignados', label: 'Arts.', align: 'center', type: 'count' },
];

function ListaPreciosPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [sinLpCount, setSinLpCount] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [result, artData] = await Promise.all([
        api('/api/lista-precios'),
        api('/api/articulos'),
      ]);
      setRows(result.data);
      if (artData?.data) {
        setSinLpCount(artData.data.filter(a => a.lp_numero == null).length);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const startEdit = (numero, field, currentValue) => {
    setEditingCell({ numero, field });
    setEditValue(currentValue != null ? String(currentValue) : '');
  };

  const confirmEdit = async () => {
    if (!editingCell) return;
    const { numero, field } = editingCell;
    const row = rows.find(r => r.numero === numero);
    if (!row) { setEditingCell(null); return; }

    let val = editValue;
    if (['precio_lista', 'precio_minimo', 'descuento_max'].includes(field)) {
      val = parseFloat(editValue.replace(',', '.'));
      if (isNaN(val)) { setEditingCell(null); return; }
    }
    if (String(row[field]) === String(val)) { setEditingCell(null); return; }

    // Auto-calcular precio_minimo al cambiar precio_lista o descuento_max
    const updates = { [field]: val };
    if (field === 'precio_lista' || field === 'descuento_max') {
      const pl = field === 'precio_lista' ? val : row.precio_lista;
      const dm = field === 'descuento_max' ? val : row.descuento_max;
      updates.precio_minimo = Math.round(pl * (1 - dm) * 100) / 100;
    }

    setRows(prev => prev.map(r => r.numero === numero ? { ...r, ...updates } : r));
    setEditingCell(null);
    try {
      await api(`/api/lista-precios/${numero}`, { method: 'PUT', body: JSON.stringify(updates) });
    } catch (err) {
      setError(err.message);
      loadData();
    }
  };

  const cancelEdit = () => { setEditingCell(null); setEditValue(''); };
  const handleKeyDown = (e) => { if (e.key === 'Enter') confirmEdit(); if (e.key === 'Escape') cancelEdit(); };

  const addRow = async () => {
    const numStr = window.prompt('Número de la nueva Lista de Precios:');
    if (!numStr) return;
    const numero = parseInt(numStr);
    if (isNaN(numero) || numero <= 0) { setError('Número inválido'); return; }
    try {
      const result = await api('/api/lista-precios', { method: 'POST', body: JSON.stringify({ numero, tipo_producto: 'Nuevo' }) });
      setRows(prev => [...prev, result.item].sort((a, b) => a.numero - b.numero));
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteRow = async (numero) => {
    if (!window.confirm(`¿Eliminar LP #${numero}?`)) return;
    setRows(prev => prev.filter(r => r.numero !== numero));
    try {
      await api(`/api/lista-precios/${numero}`, { method: 'DELETE' });
    } catch (err) {
      setError(err.message);
      loadData();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-blue-600" size={32} /></div>;
  }

  const formatCell = (row, field) => {
    const val = row[field];
    if (val == null || val === '') return <span className="text-gray-300">-</span>;
    const f = LP_FIELDS.find(c => c.key === field);
    if (f?.type === 'currency') return formatCurrency(val, row.moneda);
    if (f?.type === 'percent') return `${Math.round(val * 100)}%`;
    if (f?.type === 'count') return <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full">{val}</span>;
    return val;
  };

  const renderCell = (row, field) => {
    const f = LP_FIELDS.find(c => c.key === field);
    const isEditing = editingCell?.numero === row.numero && editingCell?.field === field;

    if (isEditing) {
      if (f?.type === 'select') {
        return (
          <select value={editValue} onChange={e => { setEditValue(e.target.value); }} onBlur={confirmEdit} autoFocus
            className="w-full text-sm bg-gray-100 border border-blue-500 text-gray-800 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">-</option>
            {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      return (
        <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={confirmEdit} onKeyDown={handleKeyDown} autoFocus
          className={`w-full text-sm bg-gray-100 border border-blue-500 text-gray-800 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-${f?.align || 'left'}`}
        />
      );
    }

    if (!f?.editable) return <span className={`text-${f?.align || 'left'} text-gray-700`}>{formatCell(row, field)}</span>;

    return (
      <span onClick={() => startEdit(row.numero, field, row[field])} className={`cursor-pointer hover:bg-gray-200 px-1.5 py-0.5 rounded block text-${f?.align || 'left'} text-gray-700`} title="Click para editar">
        {formatCell(row, field)}
      </span>
    );
  };

  return (
    <>
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-2 flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">{rows.length} listas de precios</span>
          <div className="ml-auto flex items-center gap-3">
            {sinLpCount > 0 && (
              <span className="flex items-center gap-1.5 text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-1 rounded">
                <AlertCircle size={12} /> {sinLpCount} artículos sin LP
              </span>
            )}
            <button onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
              <Plus size={14} /> Agregar
            </button>
          </div>
        </div>
      </div>
      {error && (
        <div className="px-4 mt-3">
          <div className="bg-red-50 text-red-500 border border-red-200 p-3 rounded flex items-center gap-2 text-sm">
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-500"><X size={14} /></button>
          </div>
        </div>
      )}
      <div className="px-4 py-3">
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                {LP_FIELDS.map(f => (
                  <th key={f.key} className={`text-${f.align} px-3 py-2.5 font-medium whitespace-nowrap ${f.width || ''}`}>{f.label}</th>
                ))}
                <th className="w-10 px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.numero} className={`border-b border-gray-200/50 hover:bg-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} group`}>
                  {LP_FIELDS.map(f => (
                    <td key={f.key} className={`px-3 py-1.5 whitespace-nowrap ${f.width || ''}`}>{renderCell(row, f.key)}</td>
                  ))}
                  <td className="px-2 py-1.5">
                    <button onClick={() => deleteRow(row.numero)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all" title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ============================================
// ARTICULOS PAGE
// ============================================
const BASE_ART_COLUMNS = [
  { key: 'codigo', label: 'Código', align: 'left' },
  { key: 'articulo', label: 'Artículo', align: 'left' },
  { key: 'um', label: 'UM', align: 'center', filterable: true },
  { key: 'kg_mt', label: 'KG/MT', align: 'right' },
  { key: 'diametro', label: 'Diámetro', align: 'center', filterable: true },
  { key: 'espesor', label: 'Espesor', align: 'center', filterable: true },
  { key: 'estado', label: 'Estado', align: 'center', filterable: true },
  { key: 'calidad', label: 'Calidad (Ex Cond)', align: 'center', filterable: true },
  { key: 'industria', label: 'Industria', align: 'center', filterable: true },
  { key: 'costura', label: 'Costura', align: 'center', filterable: true },
  { key: 'condicion', label: 'Condición', align: 'center', filterable: true },
  { key: 'origen', label: 'Origen', align: 'center', filterable: true },
  { key: 'proveedor', label: 'Proveedor', align: 'left', filterable: true },
  { key: 'tiene_stock', label: 'Stock', align: 'center', special: 'stock_badge' },
  { key: 'lp_asignada', label: 'LP Asignada', align: 'left', special: 'lp_select' },
];

// Cache a nivel módulo: persiste entre cambios de pestaña sin re-fetch
let _artCache = null;

function ArticulosPage() {
  const [allData, setAllData] = useState(_artCache?.allData || null);
  const [lpList, setLpList] = useState(_artCache?.lpList || []);
  const [loading, setLoading] = useState(!_artCache);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [soloConStock, setSoloConStock] = useState(false);
  const [saving, setSaving] = useState({});
  const [editingLp, setEditingLp] = useState(null);

  // Columnas base + dinámicas del Sheet (se insertan antes de Stock y LP)
  const ART_COLUMNS = useMemo(() => {
    const cols = [...BASE_ART_COLUMNS];
    if (allData?.columnasDinamicas) {
      const insertIdx = cols.findIndex(c => c.key === 'tiene_stock');
      for (const dc of allData.columnasDinamicas) {
        if (!cols.some(c => c.key === dc.key)) {
          cols.splice(insertIdx >= 0 ? insertIdx : cols.length, 0, { key: dc.key, label: dc.label, align: 'center', filterable: true });
        }
      }
    }
    return cols;
  }, [allData]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [articulos, lp] = await Promise.all([
        api('/api/articulos'),
        api('/api/lista-precios'),
      ]);
      setAllData(articulos);
      setLpList(lp.data || []);
      _artCache = { allData: articulos, lpList: lp.data || [] };
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load solo si no hay cache
  useEffect(() => { if (!_artCache) loadData(); }, [loadData]);

  const handleLpChange = async (codigo, lpNumero) => {
    setSaving(prev => ({ ...prev, [codigo]: true }));
    try {
      await api(`/api/lp-asignaciones/${encodeURIComponent(codigo)}`, {
        method: 'POST',
        body: JSON.stringify({ lp_numero: lpNumero === '' ? null : parseInt(lpNumero) })
      });
      _artCache = null;
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [codigo]: false }));
    }
  };

  // Extraer valores únicos por columna filtrable
  const filterOptions = useMemo(() => {
    if (!allData?.data) return {};
    const opts = {};
    ART_COLUMNS.filter(c => c.filterable).forEach(c => {
      const vals = new Set();
      allData.data.forEach(r => { if (r[c.key]) vals.add(r[c.key]); });
      opts[c.key] = [...vals].sort();
    });
    return opts;
  }, [allData]);

  const filteredData = useMemo(() => {
    if (!allData?.data) return [];
    const searchLower = search.toLowerCase();
    const activeFilters = Object.entries(filters).filter(([, v]) => v.length > 0);
    return allData.data.filter(row => {
      if (soloConStock && !row.tiene_stock) return false;
      if (search && !row.articulo.toLowerCase().includes(searchLower) && !row.codigo.includes(search)) return false;
      for (const [k, sel] of activeFilters) {
        if (!sel.includes(row[k])) return false;
      }
      return true;
    });
  }, [allData, search, filters, soloConStock]);

  const activeFilterCount = Object.values(filters).filter(f => f.length > 0).length;
  const hasFilters = search || soloConStock || activeFilterCount > 0;
  const clearAll = () => { setSearch(''); setFilters({}); setSoloConStock(false); setOpenFilter(null); };
  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  // Helper para obtener LP asignada (pre-computada por el backend)
  const getLpForArticle = (row) => {
    if (row.lp_numero != null) {
      const lp = lpList.find(l => l.numero === row.lp_numero);
      return { lp };
    }
    return { lp: null };
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-120px)]">
        <RefreshCw className="animate-spin text-blue-600 mb-3" size={32} />
        <p className="text-sm text-gray-500">Cargando artículos...</p>
      </div>
    );
  }

  return (
    <>
      {/* Barra de búsqueda + filtros activos */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
          <div className="relative flex-shrink-0">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar artículo o código..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm bg-gray-100 border border-gray-300 text-gray-800 placeholder-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="h-5 w-px bg-gray-100 mx-1" />
          <button
            onClick={() => setSoloConStock(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm whitespace-nowrap transition-colors ${
              soloConStock
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
          >
            <Package size={14} /> Solo con stock
          </button>
          {/* Chips de filtros activos */}
          {Object.entries(filters).filter(([, v]) => v.length > 0).map(([key, vals]) => (
            <span key={key} className="flex items-center gap-1.5 px-2 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded">
              <span className="text-gray-400">{ART_COLUMNS.find(c => c.key === key)?.label}:</span>
              {vals.length === 1 ? vals[0] : `${vals.length} sel.`}
              <button onClick={() => updateFilter(key, [])} className="hover:text-red-500"><X size={10} /></button>
            </span>
          ))}
          {hasFilters && (
            <button onClick={clearAll} className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded">
              <X size={14} /> Limpiar todo
            </button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {filteredData.length} de {allData?.totalRows || 0} artículos
            </span>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded border border-blue-300"
              title="Resincronizar datos"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Sincronizar
            </button>
            {allData?.fechaDatos && (
              <span className="text-[10px] text-emerald-600 font-medium">Datos del: {allData.fechaDatos}</span>
            )}
            {allData?.fechaActualizacion && (
              <span className="text-[10px] text-gray-400">Últ. sync: {allData.fechaActualizacion}</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 mt-3">
          <div className="bg-red-50 text-red-500 border border-red-200 p-3 rounded flex items-center gap-2 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Backdrop para cerrar dropdown de filtro */}
          {openFilter && <div className="fixed inset-0 z-40" onClick={() => setOpenFilter(null)} />}
          {filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <FileText size={48} />
              <p className="mt-2">Sin artículos para los filtros seleccionados</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-180px)]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-100 text-gray-700">
                    {ART_COLUMNS.map(c => {
                      const isFilterable = c.filterable;
                      const isFiltered = filters[c.key]?.length > 0;
                      const isOpen = openFilter === c.key;
                      return (
                        <th key={c.key} className={`relative text-${c.align} px-2 py-2.5 font-medium whitespace-nowrap text-sm`}>
                          <span className="inline-flex items-center gap-1">
                            {c.label}
                            {isFilterable && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setOpenFilter(isOpen ? null : c.key); }}
                                className={`p-0.5 rounded transition-colors ${isFiltered ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                              >
                                <Filter size={10} />
                              </button>
                            )}
                          </span>
                          {isOpen && (
                            <div className="absolute top-full left-0 z-50 mt-0.5 bg-gray-100 border border-gray-300 rounded-lg shadow-xl max-h-52 overflow-y-auto min-w-[150px] text-left font-normal" onClick={e => e.stopPropagation()}>
                              {isFiltered && (
                                <button
                                  onClick={() => { updateFilter(c.key, []); setOpenFilter(null); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border-b border-gray-300"
                                >
                                  Limpiar filtro
                                </button>
                              )}
                              {(filterOptions[c.key] || []).map(opt => (
                                <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-200 cursor-pointer text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={(filters[c.key] || []).includes(opt)}
                                    onChange={() => {
                                      const sel = filters[c.key] || [];
                                      updateFilter(c.key, sel.includes(opt) ? sel.filter(v => v !== opt) : [...sel, opt]);
                                    }}
                                    className="rounded accent-blue-500"
                                  />
                                  <span className="truncate">{opt || '(vacío)'}</span>
                                </label>
                              ))}
                              {(filterOptions[c.key] || []).length === 0 && (
                                <div className="px-3 py-2 text-xs text-gray-400">Sin opciones</div>
                              )}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, i) => {
                    const { lp: assignedLp } = getLpForArticle(row);
                    const isEditingThis = editingLp === row.codigo;
                    return (
                      <tr key={row.codigo + '-' + i} className={`border-b border-gray-200/50 hover:bg-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        {ART_COLUMNS.map(c => (
                          <td key={c.key} className={`px-2 py-1.5 whitespace-nowrap text-${c.align} text-sm text-gray-600`}>
                            {c.special === 'stock_badge' ? (
                              row.tiene_stock ? (
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="En stock" />
                              ) : (
                                <span className="inline-block w-2 h-2 rounded-full bg-gray-200" title="Sin stock" />
                              )
                            ) : c.special === 'lp_select' ? (
                              isEditingThis ? (
                                <div className="flex items-center gap-1">
                                  <select
                                    autoFocus
                                    value={assignedLp?.numero ?? ''}
                                    onChange={(e) => { handleLpChange(row.codigo, e.target.value); setEditingLp(null); }}
                                    onBlur={() => setEditingLp(null)}
                                    className="text-xs py-0.5 px-1 bg-gray-100 border border-blue-500 rounded max-w-[220px] text-gray-800"
                                  >
                                    <option value="">-- Sin asignar --</option>
                                    {lpList.map(lp => (
                                      <option key={lp.numero} value={lp.numero}>#{lp.numero} - {lp.tipo_producto}</option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <span
                                  onClick={() => setEditingLp(row.codigo)}
                                  className={`cursor-pointer px-1.5 py-0.5 rounded text-xs inline-block ${
                                    saving[row.codigo] ? 'opacity-50' :
                                    assignedLp ? 'text-blue-600 bg-blue-50 border border-blue-200' :
                                    'text-gray-300 hover:bg-gray-100'
                                  }`}
                                  title={assignedLp ? `LP #${assignedLp.numero} (click para cambiar)` : 'Click para asignar'}
                                >
                                  {saving[row.codigo] ? '...' : assignedLp ? `#${assignedLp.numero} - ${assignedLp.tipo_producto}` : '—'}
                                </span>
                              )
                            ) : (
                              row[c.key] || <span className="text-gray-300">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
// ============================================
// KPI Card with useCountUp
// ============================================
function KpiCard({ label, value, total, color, suffix, hasFilters }) {
  const animated = useCountUp(value);
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color }}>{fmtNum.format(animated)}</p>
      {hasFilters && (
        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>{Math.round(pct)}% del total</span>
            <span>{fmtNum.format(total)}{suffix}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// DASHBOARD PAGE
// ============================================

function DashboardFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const hasSelection = selected.length > 0;
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${hasSelection ? 'bg-blue-50 border-blue-300 text-blue-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'}`}
      >
        <Filter size={12} />
        {label}
        {hasSelection && <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold leading-none">{selected.length}</span>}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-gray-100 border border-gray-300 rounded-lg shadow-xl min-w-[180px] max-h-64 overflow-y-auto py-1">
            {options.map(opt => {
              const isSel = selected.includes(opt);
              return (
                <label key={opt} className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs transition-colors ${isSel ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(opt)} className="rounded accent-blue-500 flex-shrink-0" />
                  <span className="truncate">{opt}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function DashboardPage() {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ condicion: [], industria: [], tipo: [] });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await api('/api/stock');
        setStockData(data);
      } catch (err) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!stockData?.data) return [];
    return stockData.data.filter(row => {
      if (filters.condicion.length > 0 && !filters.condicion.includes(row.condicion)) return false;
      if (filters.industria.length > 0 && !filters.industria.includes(row.industria)) return false;
      if (filters.tipo.length > 0 && !filters.tipo.includes(row.tipo)) return false;
      return true;
    });
  }, [stockData, filters]);

  const totalStats = useMemo(() => {
    if (!stockData?.data) return { metros: 0, kilos: 0, unidades: 0, total: 0 };
    let metros = 0, kilos = 0, unidades = 0;
    for (const i of stockData.data) {
      const um = (i.um || '').toLowerCase();
      const sn = i.stock_neto_np || 0;
      if (um.includes('mt')) metros += sn;
      else if (um === 'kg') kilos += sn;
      else if (um === 'un' || um === 'un.') unidades += sn;
    }
    return { metros, kilos, unidades, total: stockData.data.length };
  }, [stockData]);

  const stats = useMemo(() => {
    let metros = 0, kilos = 0, unidades = 0;
    for (const i of filtered) {
      const um = (i.um || '').toLowerCase();
      const sn = i.stock_neto_np || 0;
      if (um.includes('mt')) metros += sn;
      else if (um === 'kg') kilos += sn;
      else if (um === 'un' || um === 'un.') unidades += sn;
    }
    return { metros, kilos, unidades, total: filtered.length };
  }, [filtered]);

  const tipoStats = useMemo(() => {
    const map = {};
    for (const item of filtered) {
      const t = item.tipo || 'Sin tipo';
      if (!map[t]) map[t] = 0;
      map[t]++;
    }
    return Object.entries(map)
      .map(([tipo, count]) => ({ tipo, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const condicionStats = useMemo(() => {
    const map = {};
    for (const item of filtered) {
      const c = item.condicion || 'Sin condición';
      if (!map[c]) map[c] = { count: 0, metros: 0, kilos: 0, unidades: 0 };
      map[c].count++;
      const um = (item.um || '').toLowerCase();
      const sn = item.stock_neto_np || 0;
      if (um.includes('mt')) map[c].metros += sn;
      else if (um === 'kg') map[c].kilos += sn;
      else if (um === 'un' || um === 'un.') map[c].unidades += sn;
    }
    return Object.entries(map)
      .map(([condicion, data]) => ({ condicion, ...data }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const topMas = useMemo(() => {
    return [...filtered].sort((a, b) => (b.stock_neto_np || 0) - (a.stock_neto_np || 0)).slice(0, 10);
  }, [filtered]);

  const topMenos = useMemo(() => {
    return [...filtered].sort((a, b) => (a.stock_neto_np || 0) - (b.stock_neto_np || 0)).slice(0, 10);
  }, [filtered]);

  const toggleFilter = (key, value) => {
    setFilters(f => {
      const arr = f[key] || [];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...f, [key]: next };
    });
  };

  const hasFilters = filters.condicion.length > 0 || filters.industria.length > 0 || filters.tipo.length > 0;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500"><RefreshCw size={20} className="animate-spin mr-2" /> Cargando...</div>;

  const CHART_COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];
  const maxMas = topMas.length > 0 ? Math.max(...topMas.map(i => Math.abs(i.stock_neto_np || 0)), 1) : 1;
  const maxMenos = topMenos.length > 0 ? Math.max(...topMenos.map(i => Math.abs(i.stock_neto_np || 0)), 1) : 1;
  const maxTipo = tipoStats.length > 0 ? Math.max(...tipoStats.map(t => t.count), 1) : 1;
  const condMaxMetros = Math.max(...condicionStats.map(c => c.metros), 1);
  const condMaxKilos = Math.max(...condicionStats.map(c => c.kilos), 1);
  const condMaxUnidades = Math.max(...condicionStats.map(c => c.unidades), 1);

  const kpiCards = [
    { label: 'Artículos en stock', value: stats.total, total: totalStats.total, color: '#06b6d4', suffix: '' },
    { label: 'Total Metros', value: stats.metros, total: totalStats.metros, color: '#10b981', suffix: ' mt' },
    { label: 'Total Kilos', value: stats.kilos, total: totalStats.kilos, color: '#f59e0b', suffix: ' kg' },
    { label: 'Total Unidades', value: stats.unidades, total: totalStats.unidades, color: '#8b5cf6', suffix: ' un' },
  ];

  return (
    <div className="h-[calc(100vh-56px)] overflow-auto p-4">
      {/* Header + Filtros inline */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-700 flex items-center gap-2"><BarChart3 size={20} className="text-blue-600" /> Dashboard</h2>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <DashboardFilter label="Condición" options={stockData?.filters?.condicion || []} selected={filters.condicion} onChange={v => setFilters(f => ({ ...f, condicion: v }))} />
          <DashboardFilter label="Industria" options={stockData?.filters?.industria || []} selected={filters.industria} onChange={v => setFilters(f => ({ ...f, industria: v }))} />
          <DashboardFilter label="Tipo" options={stockData?.filters?.tipo || []} selected={filters.tipo} onChange={v => setFilters(f => ({ ...f, tipo: v }))} />
          {hasFilters && (
            <button onClick={() => setFilters({ condicion: [], industria: [], tipo: [] })} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 border border-red-300 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards with animated counters */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {kpiCards.map(c => (
          <KpiCard key={c.label} {...c} hasFilters={hasFilters} />
        ))}
      </div>

      {/* Charts: TIPO distribution + Condición breakdown */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* TIPO Distribution - interactive */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Package size={16} /> Distribución por Tipo
          </h3>
          {tipoStats.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Sin datos</p>
          ) : (
            <div className="space-y-1">
              {tipoStats.map((t, idx) => {
                const pct = (t.count / maxTipo) * 100;
                const color = CHART_COLORS[idx % CHART_COLORS.length];
                const isActive = filters.tipo.includes(t.tipo);
                return (
                  <div
                    key={t.tipo}
                    onClick={() => toggleFilter('tipo', t.tipo)}
                    className={`relative cursor-pointer rounded-lg transition-all duration-200 ${isActive ? 'ring-1 ring-cyan-400/60' : 'hover:bg-gray-50'}`}
                  >
                    <div className="absolute inset-0 rounded-lg" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.15 }} />
                    <div className="relative flex items-center gap-2 px-3 py-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="flex-1 text-gray-600 font-medium truncate">{t.tipo}</span>
                      <span className="text-gray-500 tabular-nums font-bold">{t.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {filters.tipo.length > 0 && (
            <p className="text-[10px] text-blue-600/60 mt-2 text-center">Click en una barra para quitar filtro</p>
          )}
          {filters.tipo.length === 0 && tipoStats.length > 0 && (
            <p className="text-[10px] text-gray-400 mt-2 text-center">Click en una barra para filtrar</p>
          )}
        </div>

        {/* Condición Breakdown - interactive */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Filter size={16} /> Stock por Condición
          </h3>
          {condicionStats.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Sin datos</p>
          ) : (
            <div className="space-y-2.5">
              {condicionStats.map(c => {
                const isActive = filters.condicion.includes(c.condicion);
                return (
                  <div
                    key={c.condicion}
                    onClick={() => toggleFilter('condicion', c.condicion)}
                    className={`rounded-lg p-2.5 transition-all duration-200 cursor-pointer ${isActive ? 'bg-gray-100 ring-1 ring-emerald-400/60' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-gray-600">{c.condicion}</span>
                      <span className="text-[10px] text-gray-400">{c.count} artículos</span>
                    </div>
                    <div className="space-y-1">
                      {c.metros > 0 && (
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="w-12 text-gray-400">Metros</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(c.metros / condMaxMetros) * 100}%`, backgroundColor: '#10b981' }} />
                          </div>
                          <span className="w-16 text-right tabular-nums" style={{ color: '#10b981' }}>{fmtNum.format(c.metros)}</span>
                        </div>
                      )}
                      {c.kilos > 0 && (
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="w-12 text-gray-400">Kilos</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(c.kilos / condMaxKilos) * 100}%`, backgroundColor: '#f59e0b' }} />
                          </div>
                          <span className="w-16 text-right tabular-nums" style={{ color: '#f59e0b' }}>{fmtNum.format(c.kilos)}</span>
                        </div>
                      )}
                      {c.unidades > 0 && (
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="w-12 text-gray-400">Unidades</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(c.unidades / condMaxUnidades) * 100}%`, backgroundColor: '#8b5cf6' }} />
                          </div>
                          <span className="w-16 text-right tabular-nums" style={{ color: '#8b5cf6' }}>{fmtNum.format(c.unidades)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {filters.condicion.length > 0 && (
            <p className="text-[10px] text-emerald-600/60 mt-2 text-center">Click en una tarjeta para quitar filtro</p>
          )}
          {filters.condicion.length === 0 && condicionStats.length > 0 && (
            <p className="text-[10px] text-gray-400 mt-2 text-center">Click en una tarjeta para filtrar</p>
          )}
        </div>
      </div>

      {/* Top 10 grids */}
      <div className="grid grid-cols-2 gap-4">
        {/* Top 10 MÁS stock */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-emerald-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <TrendingUp size={16} /> Top 10 — Más stock
          </h3>
          <div className="space-y-1.5">
            {topMas.map((item, idx) => {
              const pct = Math.abs(item.stock_neto_np || 0) / maxMas * 100;
              return (
                <div key={item.codigo} className="relative">
                  <div className="absolute inset-0 rounded bg-emerald-500/10" style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center gap-2 px-2 py-1.5 text-xs">
                    <span className="text-gray-400 w-4 text-right font-mono">{idx + 1}</span>
                    <span className="flex-1 text-gray-600 truncate">{item.articulo}</span>
                    <span className="text-gray-400">{item.um || ''}</span>
                    <span className="text-emerald-600 font-bold tabular-nums w-20 text-right">{fmtNum.format(item.stock_neto_np || 0)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 10 MENOS stock */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-red-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <TrendingDown size={16} /> Top 10 — Menos stock
          </h3>
          <div className="space-y-1.5">
            {topMenos.map((item, idx) => {
              const pct = Math.abs(item.stock_neto_np || 0) / maxMenos * 100;
              return (
                <div key={item.codigo} className="relative">
                  <div className="absolute inset-0 rounded bg-red-500/10" style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center gap-2 px-2 py-1.5 text-xs">
                    <span className="text-gray-400 w-4 text-right font-mono">{idx + 1}</span>
                    <span className="flex-1 text-gray-600 truncate">{item.articulo}</span>
                    <span className="text-gray-400">{item.um || ''}</span>
                    <span className="text-red-500 font-bold tabular-nums w-20 text-right">{fmtNum.format(item.stock_neto_np || 0)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CHECKLIST PAGE
// ============================================
const CHECKLIST_CATEGORIES = ['General', 'Precios', 'Stock', 'Sistema', 'Datos'];

function ChecklistSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('General');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showDone, setShowDone] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api('/api/checklist');
      setItems(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const addItem = async () => {
    if (!newText.trim()) return;
    try {
      const result = await api('/api/checklist', {
        method: 'POST',
        body: JSON.stringify({ text: newText.trim(), category: newCategory })
      });
      setItems(prev => [...prev, result.item]);
      setNewText('');
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleDone = async (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    try {
      await api(`/api/checklist/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ done: !item.done })
      });
      setItems(prev => prev.map(i => i.id === id ? { ...i, done: !i.done } : i));
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteItem = async (id) => {
    try {
      await api(`/api/checklist/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  const confirmEdit = async () => {
    if (!editingId || !editText.trim()) { setEditingId(null); return; }
    try {
      await api(`/api/checklist/${editingId}`, {
        method: 'PUT',
        body: JSON.stringify({ text: editText.trim() })
      });
      setItems(prev => prev.map(i => i.id === editingId ? { ...i, text: editText.trim() } : i));
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const filtered = items.filter(i => {
    if (filterCategory && i.category !== filterCategory) return false;
    if (!showDone && i.done) return false;
    return true;
  });

  const pendingCount = items.filter(i => !i.done).length;
  const doneCount = items.filter(i => i.done).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded">{pendingCount}</span>
          <span className="text-sm text-gray-500">pendientes</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded">{doneCount}</span>
          <span className="text-sm text-gray-500">completados</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowDone(v => !v)}
            className={`text-xs px-2.5 py-1.5 rounded border transition-colors ${showDone ? 'bg-gray-100 border-gray-300 text-gray-600' : 'bg-white border-gray-200 text-gray-400'}`}
          >
            {showDone ? 'Ocultar completados' : 'Mostrar completados'}
          </button>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="text-xs bg-gray-100 border border-gray-300 text-gray-700 rounded px-2 py-1.5"
          >
            <option value="">Todas las categorías</option>
            {CHECKLIST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 border border-red-200 p-2 rounded flex items-center gap-2 text-sm">
          <AlertCircle size={14} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={12} /></button>
        </div>
      )}

      {/* Add new item */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-2">
        <select
          value={newCategory}
          onChange={e => setNewCategory(e.target.value)}
          className="text-sm bg-gray-100 border border-gray-300 text-gray-700 rounded px-2 py-1.5 flex-shrink-0"
        >
          {CHECKLIST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="text"
          placeholder="Agregar nuevo item..."
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addItem()}
          className="flex-1 bg-gray-100 border border-gray-300 text-gray-800 placeholder-gray-400 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={addItem}
          disabled={!newText.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
        >
          <Plus size={14} /> Agregar
        </button>
      </div>

      {/* Items list */}
      <div className="space-y-1">
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CheckSquare size={36} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay items en la lista</p>
          </div>
        ) : (
          filtered.map(item => (
            <div
              key={item.id}
              className={`bg-white border rounded-lg px-3 py-2.5 flex items-start gap-3 group transition-colors ${item.done ? 'border-gray-200/50 opacity-60' : 'border-gray-200'}`}
            >
              <button
                onClick={() => toggleDone(item.id)}
                className={`mt-0.5 flex-shrink-0 transition-colors ${item.done ? 'text-emerald-500' : 'text-gray-400 hover:text-blue-600'}`}
              >
                {item.done ? <CheckSquare size={18} /> : <Square size={18} />}
              </button>
              <div className="flex-1 min-w-0">
                {editingId === item.id ? (
                  <input
                    type="text"
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onBlur={confirmEdit}
                    onKeyDown={e => { if (e.key === 'Enter') confirmEdit(); if (e.key === 'Escape') setEditingId(null); }}
                    autoFocus
                    className="w-full bg-gray-100 border border-blue-500 text-gray-800 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  <span className={`text-sm ${item.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {item.text}
                  </span>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                    item.category === 'Precios' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                    item.category === 'Stock' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                    item.category === 'Sistema' ? 'bg-purple-50 text-purple-600 border-purple-200' :
                    item.category === 'Datos' ? 'bg-blue-50 text-blue-600 border-blue-200/50' :
                    'bg-gray-100 text-gray-500 border-gray-300'
                  }`}>{item.category}</span>
                  {item.createdBy && <span className="text-[10px] text-gray-300">{item.createdBy}</span>}
                  {item.createdAt && <span className="text-[10px] text-gray-300">{new Date(item.createdAt).toLocaleDateString('es-AR')}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {!item.done && (
                  <button onClick={() => startEdit(item)} className="p-1 text-gray-400 hover:text-blue-600" title="Editar">
                    <Edit3 size={14} />
                  </button>
                )}
                <button onClick={() => deleteItem(item.id)} className="p-1 text-gray-400 hover:text-red-500" title="Eliminar">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================
// STOCK CHILE
// ============================================
const CHILE_COLUMNS = [
  { key: 'codigo', label: 'Código', align: 'left', sticky: true },
  { key: 'descripcion', label: 'Descripción', align: 'left' },
  { key: 'dn_fracc', label: 'DN', align: 'center' },
  { key: 'sch', label: 'SCH', align: 'center' },
  { key: 'std_xs', label: 'STD/XS', align: 'center' },
  { key: 'tira', label: 'Tira', align: 'center' },
  { key: 'disponible', label: 'Disponible', align: 'right' },
  { key: 'unidad', label: 'UM', align: 'center' },
  { key: 'disponible_mts', label: 'Mts', align: 'right' },
  { key: 'disponible_kgs', label: 'Kgs', align: 'right' },
  { key: 'rel_kg_mt', label: 'Kg/Mt', align: 'right' },
];

let _chileCache = null;

function StockChilePage() {
  const [chileData, setChileData] = useState(_chileCache);
  const [loading, setLoading] = useState(!_chileCache);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [chileSearch, setChileSearch] = useState('');
  const [sortCol, setSortCol] = useState('descripcion');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({ tipo: [], dn: [], espesor: [], sch: [] });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const d = await api('/api/stock-chile');
      setChileData(d);
      _chileCache = d;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!_chileCache) loadData(); }, [loadData]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  // Cascada: Tipo → DN → Espesor (cruzada con DN)
  const cascadeOptions = useMemo(() => {
    if (!chileData?.data) return { tipo: [], dn: [], espesor: [], sch: [] };
    const allRows = chileData.data;

    const tipoOpts = [...new Set(allRows.map(r => r.tipo).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));

    const afterTipo = filters.tipo.length > 0 ? allRows.filter(r => filters.tipo.includes(r.tipo)) : allRows;

    // DN y Espesor con cascada cruzada
    const afterEspesor = filters.espesor.length > 0 ? afterTipo.filter(r => filters.espesor.includes(r.espesor)) : afterTipo;
    const dnOpts = [...new Set(afterEspesor.map(r => r.dn_fracc).filter(Boolean))].sort((a, b) => {
      const na = parseDiamNum(a), nb = parseDiamNum(b);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      return a.localeCompare(b, 'es');
    });

    const afterDN = filters.dn.length > 0 ? afterTipo.filter(r => filters.dn.includes(r.dn_fracc)) : afterTipo;
    const espesorOpts = [...new Set(afterDN.map(r => r.espesor).filter(v => v && v !== '. . .'))].sort((a, b) => {
      const na = parseFloat(String(a).replace(',', '.')), nb = parseFloat(String(b).replace(',', '.'));
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, 'es');
    });

    // SCH: filtrado por tipo + DN + espesor
    const afterAll = [afterTipo, filters.dn.length > 0 ? afterTipo.filter(r => filters.dn.includes(r.dn_fracc)) : null, filters.espesor.length > 0 ? afterTipo.filter(r => filters.espesor.includes(r.espesor)) : null]
      .filter(Boolean).reduce((a, b) => a.filter(r => b.includes(r)), afterTipo);
    const schOpts = [...new Set(afterAll.map(r => r.sch).filter(v => v && v !== '. . .'))].sort((a, b) => {
      const na = parseInt(a), nb = parseInt(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, 'es');
    });

    return { tipo: tipoOpts, dn: dnOpts, espesor: espesorOpts, sch: schOpts };
  }, [chileData, filters.tipo, filters.dn, filters.espesor]);

  const filteredData = useMemo(() => {
    if (!chileData?.data) return [];
    return chileData.data.filter(row => {
      if (chileSearch) {
        const q = chileSearch.toLowerCase();
        if (!row.descripcion?.toLowerCase().includes(q) && !row.codigo?.toLowerCase().includes(q)) return false;
      }
      if (filters.tipo.length > 0 && !filters.tipo.includes(row.tipo)) return false;
      if (filters.dn.length > 0 && !filters.dn.includes(row.dn_fracc)) return false;
      if (filters.espesor.length > 0 && !filters.espesor.includes(row.espesor)) return false;
      if (filters.sch.length > 0 && !filters.sch.includes(row.sch)) return false;
      return true;
    });
  }, [chileData, filters, chileSearch]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      va = String(va || ''); vb = String(vb || '');
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filteredData, sortCol, sortDir]);

  const hasFilters = chileSearch || Object.values(filters).some(f => f.length > 0);
  const clearFilters = () => { setChileSearch(''); setFilters({ tipo: [], dn: [], espesor: [], sch: [] }); };
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const totalMts = filteredData.reduce((s, r) => s + (r.disponible_mts || 0), 0);
  const totalKgs = filteredData.reduce((s, r) => s + (r.disponible_kgs || 0), 0);
  const totalUnits = filteredData.reduce((s, r) => s + (r.disponible || 0), 0);

  const formatCell = (row, col) => {
    const v = row[col.key];
    if (v == null) return <span className="text-gray-300">-</span>;
    if (col.key === 'disponible' || col.key === 'disponible_mts' || col.key === 'disponible_kgs') return fmtStock.format(v);
    if (col.key === 'rel_kg_mt') return fmtStock.format(v);
    return v;
  };

  // Cerrar dropdowns al click fuera
  useEffect(() => {
    const handler = (e) => {
      document.querySelectorAll('.chile-dropdown:not(.hidden)').forEach(el => {
        if (!el.parentElement.contains(e.target)) el.classList.add('hidden');
      });
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Header: búsqueda + stats */}
      <div className="bg-white border-b border-gray-200 text-gray-800 px-4 py-2 flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder="Buscar artículo..." value={chileSearch} onChange={e => setChileSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm bg-gray-100 border border-gray-300 text-gray-800 placeholder-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-56" />
          {chileSearch && <button onClick={() => setChileSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"><X size={12} /></button>}
        </div>
        <div className="h-8 w-px bg-gray-100" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{filteredData.length}</span>
          <span className="text-xs text-gray-400">artículos</span>
          {hasFilters && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded">filtrado</span>}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-4 text-[11px] text-gray-500">
            <span>Unid: <b className="text-gray-700">{fmtStock.format(totalUnits)}</b></span>
            <span>Mts: <b className="text-gray-700">{fmtStock.format(totalMts)}</b></span>
            <span>Kgs: <b className="text-gray-700">{fmtStock.format(totalKgs)}</b></span>
          </div>
          {chileData?.fechaDatos && <span className="text-[10px] text-emerald-600 font-medium">Datos del: {chileData.fechaDatos}</span>}
          {chileData?.fechaActualizacion && <span className="text-[10px] text-gray-400">Últ. sync: {chileData.fechaActualizacion}</span>}
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 border border-red-300 rounded-lg transition-colors">
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Filtros en cascada: Tipo como dropdown */}
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-4 relative z-30 overflow-visible">
        {(() => {
          const opts = cascadeOptions.tipo || [];
          const sel = filters.tipo || [];
          return (
            <div className="flex items-center gap-1.5 flex-shrink-0 relative">
              <span className="text-[10px] uppercase tracking-widest font-bold mr-1 text-amber-600">Tipo</span>
              <button
                onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-md border transition-all cursor-pointer ${sel.length > 0 ? 'bg-amber-100 text-amber-700 border-amber-400 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 shadow-sm'}`}
              >
                {sel.length === 0 ? 'Todos' : sel.length === 1 ? sel[0].replace(/^\d+\.\s*/, '') : `${sel.length} sel.`}
                <ChevronDown size={11} />
              </button>
              <div className="chile-dropdown hidden absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-[280px] max-h-[350px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {opts.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400">Sin opciones</div>
                ) : opts.map(opt => {
                  const active = sel.includes(opt);
                  return (
                    <button key={opt}
                      onClick={() => {
                        const next = active ? sel.filter(v => v !== opt) : [...sel, opt];
                        updateFilter('tipo', next);
                        updateFilter('dn', []); updateFilter('espesor', []); updateFilter('sch', []);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${active ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-600'}`}
                    >
                      {active ? '● ' : '○ '}{opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {filters.tipo.length > 0 && (
          <button onClick={() => { setFilters({ tipo: [], dn: [], espesor: [], sch: [] }); }}
            className="flex items-center gap-1 px-2 py-1 text-[10px] text-red-500 hover:bg-red-50 rounded border border-red-300 flex-shrink-0 ml-auto">
            <X size={10} /> Limpiar
          </button>
        )}
      </div>

      {error && (
        <div className="px-3 py-2">
          <div className="bg-red-50 text-red-500 p-2 rounded-lg flex items-center gap-2 text-xs border border-red-200">
            <AlertCircle size={14} /> {error}
          </div>
        </div>
      )}

      {/* Main: Tabla + Filtros laterales (DN + Espesor) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Tabla */}
        <div className="flex-1 min-w-0 p-3 overflow-hidden">
          <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-auto h-full">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
                  {CHILE_COLUMNS.map(col => (
                    <th key={col.key} onClick={() => handleSort(col.key)}
                      className={`text-${col.align} px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-[#003d70] transition-colors whitespace-nowrap ${col.sticky ? 'sticky left-0 bg-[#004a8a] z-10' : ''}`}>
                      <span className="inline-flex items-center gap-1">
                        {col.align === 'right' && sortCol === col.key && <span className="text-yellow-300 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                        {col.label}
                        {col.align !== 'right' && sortCol === col.key && <span className="text-yellow-300 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <tr key={row.codigo + '-' + i} className={`border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    {CHILE_COLUMNS.map(col => (
                      <td key={col.key} className={`text-${col.align} px-3 py-2 whitespace-nowrap ${col.sticky ? 'sticky left-0 bg-inherit z-10 font-mono text-[11px]' : ''} ${col.key === 'disponible_kgs' ? 'font-semibold' : ''} ${col.key === 'descripcion' ? 'max-w-[300px] truncate' : ''}`}>
                        {formatCell(row, col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Toggle filtros laterales */}
        <button onClick={() => setShowFilters(prev => !prev)}
          className="flex-shrink-0 w-6 bg-white border-l border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
          title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}>
          {showFilters ? <ChevronRight size={14} className="text-gray-500" /> : <ChevronLeft size={14} className="text-gray-500" />}
        </button>

        {/* Panel lateral: DN y Espesor como botones */}
        {showFilters && (
          <div className="w-[200px] flex-shrink-0 border-l border-blue-200 overflow-y-auto p-3 bg-blue-50" style={{ scrollbarWidth: 'none' }}>
            {/* DN Chico */}
            {(() => {
              const allDN = cascadeOptions.dn || [];
              const chico = allDN.filter(d => { const n = parseDiamNum(d); return n !== null && n < 4; });
              const grande = allDN.filter(d => { const n = parseDiamNum(d); return n !== null && n >= 4; });
              const otros = allDN.filter(d => parseDiamNum(d) === null);
              return (
                <>
                  <div className="mb-3">
                    <span className="text-[10px] text-blue-800 uppercase tracking-widest font-bold">{'DN Chico (<4")'}</span>
                    <div className="grid grid-cols-2 gap-1 mt-1.5">
                      {chico.map(opt => {
                        const active = filters.dn.includes(opt);
                        return (
                          <button key={opt} onClick={() => updateFilter('dn', active ? filters.dn.filter(v => v !== opt) : [...filters.dn, opt])}
                            className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 shadow-sm'}`}
                          >{opt}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mb-3">
                    <span className="text-[10px] text-blue-800 uppercase tracking-widest font-bold">{'DN Grande (≥4")'}</span>
                    <div className="grid grid-cols-2 gap-1 mt-1.5">
                      {[...grande, ...otros].map(opt => {
                        const active = filters.dn.includes(opt);
                        return (
                          <button key={opt} onClick={() => updateFilter('dn', active ? filters.dn.filter(v => v !== opt) : [...filters.dn, opt])}
                            className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 shadow-sm'}`}
                          >{opt}</button>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
            {/* Espesor */}
            {(cascadeOptions.espesor || []).length > 0 && (
              <div>
                <span className="text-[10px] text-blue-800 uppercase tracking-widest font-bold">Espesor</span>
                <div className="grid grid-cols-2 gap-1 mt-1.5">
                  {(cascadeOptions.espesor || []).map(opt => {
                    const active = filters.espesor.includes(opt);
                    return (
                      <button key={opt} onClick={() => updateFilter('espesor', active ? filters.espesor.filter(v => v !== opt) : [...filters.espesor, opt])}
                        className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 shadow-sm'}`}
                      >{opt}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* SCH */}
            {(cascadeOptions.sch || []).length > 0 && (
              <div className="mt-3">
                <span className="text-[10px] text-blue-800 uppercase tracking-widest font-bold">SCH</span>
                <div className="grid grid-cols-2 gap-1 mt-1.5">
                  {(cascadeOptions.sch || []).map(opt => {
                    const active = filters.sch.includes(opt);
                    return (
                      <button key={opt} onClick={() => updateFilter('sch', active ? filters.sch.filter(v => v !== opt) : [...filters.sch, opt])}
                        className={`px-1.5 py-1 text-[11px] rounded border transition-all cursor-pointer text-center truncate ${active ? 'bg-blue-100 text-blue-700 border-blue-400 shadow-sm font-medium' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-800 shadow-sm'}`}
                      >{opt}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================
// NORMAS (ASME B36.10M / API 5CT TBG / CSG)
// ============================================
function NormasPage() {
  const [tab, setTab] = useState('asme');
  const [asmeData, setAsmeData] = useState(null);
  const [tbgData, setTbgData] = useState(null);
  const [csgData, setCsgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterDN, setFilterDN] = useState([]);
  const [filterESP, setFilterESP] = useState([]);
  const [filterSCH, setFilterSCH] = useState([]);
  const [sortCol, setSortCol] = useState('dn');
  const [sortDir, setSortDir] = useState('asc');

  const fmt2dec = useMemo(() => new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const loadTab = useCallback(async (t) => {
    try {
      setLoading(true); setError('');
      if (t === 'asme' && !asmeData) { const d = await api('/api/tabla-asme'); setAsmeData(d); }
      else if (t === 'tbg' && !tbgData) { const d = await api('/api/tabla-tbg'); setTbgData(d); }
      else if (t === 'csg' && !csgData) { const d = await api('/api/tabla-csg'); setCsgData(d); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [asmeData, tbgData, csgData]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const switchTab = (t) => { setTab(t); setSearch(''); setFilterDN([]); setFilterESP([]); setFilterSCH([]); setSortCol(t === 'asme' ? 'dn' : 'label'); setSortDir('asc'); };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortHeader = ({ col, label, align = 'center' }) => (
    <th onClick={() => handleSort(col)}
      className={`text-${align} px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-[#003d70] transition-colors whitespace-nowrap`}>
      <span className="inline-flex items-center gap-1">{label}{sortCol === col && <span className="text-yellow-300 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}</span>
    </th>
  );

  const sortFn = (a, b) => {
    let va = a[sortCol], vb = b[sortCol];
    if (typeof va === 'number' || typeof vb === 'number') return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    if (sortCol === 'dn') { const na = parseDiamNum(va), nb = parseDiamNum(vb); if (na !== null && nb !== null) return sortDir === 'asc' ? na - nb : nb - na; }
    va = String(va || ''); vb = String(vb || '');
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  };

  // ASME filtering
  const asmeFiltered = useMemo(() => {
    if (!asmeData?.tabla) return [];
    let rows = asmeData.tabla;
    if (search) { const s = search.toLowerCase(); rows = rows.filter(r => r.concatenar?.toLowerCase().includes(s) || r.dn?.toLowerCase().includes(s)); }
    if (filterDN.length > 0) rows = rows.filter(r => filterDN.includes(r.dn));
    if (filterESP.length > 0) rows = rows.filter(r => filterESP.includes(r.esp));
    if (filterSCH.length > 0) rows = rows.filter(r => filterSCH.includes(r.sch));
    return [...rows].sort(sortFn);
  }, [asmeData, search, filterDN, filterESP, filterSCH, sortCol, sortDir]);

  // TBG/CSG filtering
  const filteredTbgCsg = useMemo(() => {
    const src = tab === 'tbg' ? tbgData?.tabla : tab === 'csg' ? csgData?.tabla : null;
    if (!src?.length) return [];
    let rows = src;
    if (search) { const s = search.toLowerCase(); rows = rows.filter(r => r.label?.toLowerCase().includes(s)); }
    return [...rows].sort(sortFn);
  }, [tab, tbgData, csgData, search, sortCol, sortDir]);

  const TABS = [
    { id: 'asme', label: 'ASME B36.10M', count: asmeData?.totalRegistros },
    { id: 'tbg', label: 'API 5CT TBG', count: tbgData?.totalRegistros },
    { id: 'csg', label: 'API 5CT CSG', count: csgData?.totalRegistros },
  ];

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {TABS.map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${tab === t.id ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label} {t.count != null && <span className="ml-1 text-[10px] text-gray-400">({t.count})</span>}
            </button>
          ))}
        </div>
        <div className="relative flex-shrink-0">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input type="text" placeholder={tab === 'asme' ? 'Buscar DN...' : 'Buscar...'} value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm bg-gray-100 border border-gray-300 text-gray-800 placeholder-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-48" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"><X size={12} /></button>}
        </div>
        <span className="text-xs text-gray-400">{tab === 'asme' ? asmeFiltered.length : filteredTbgCsg.length} registros</span>
      </div>

      {tab === 'asme' && asmeData && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-3">
          {[{ key: 'dn', label: 'DN', opts: asmeData.filters?.dn || [], sel: filterDN, set: setFilterDN, color: 'bg-blue-100 text-blue-700 border-blue-400' },
            { key: 'esp', label: 'Espesor', opts: asmeData.filters?.espesor || [], sel: filterESP, set: setFilterESP, color: 'bg-amber-100 text-amber-700 border-amber-400' },
            { key: 'sch', label: 'SCH', opts: asmeData.filters?.sch || [], sel: filterSCH, set: setFilterSCH, color: 'bg-purple-100 text-purple-700 border-purple-400' }
          ].map(f => (
            <div key={f.key} className="flex items-center gap-1.5 flex-shrink-0 relative">
              <span className="text-[10px] uppercase tracking-widest font-bold mr-1 text-blue-800">{f.label}</span>
              <button onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-md border transition-all cursor-pointer ${f.sel.length > 0 ? `${f.color} shadow-sm` : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 shadow-sm'}`}>
                {f.sel.length === 0 ? 'Todos' : f.sel.length === 1 ? f.sel[0] : `${f.sel.length} sel.`}
                <ChevronDown size={11} />
              </button>
              <div className="hidden absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-[120px] max-h-[300px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {f.opts.map(opt => {
                  const active = f.sel.includes(opt);
                  return (
                    <button key={opt} onClick={() => f.set(prev => prev.includes(opt) ? prev.filter(v => v !== opt) : [...prev, opt])}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                      {active ? '● ' : '○ '}{opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {(filterDN.length > 0 || filterESP.length > 0 || filterSCH.length > 0) && (
            <button onClick={() => { setFilterDN([]); setFilterESP([]); setFilterSCH([]); }} className="text-[10px] text-red-500 hover:underline ml-1">Limpiar</button>
          )}
        </div>
      )}

      {error && <div className="px-3 py-2"><div className="bg-red-50 text-red-500 p-2 rounded-lg text-xs border border-red-200"><AlertCircle size={14} className="inline mr-1" />{error}</div></div>}

      <div className="flex-1 overflow-auto p-3">
        {loading && !asmeData && !tbgData && !csgData ? (
          <div className="flex items-center justify-center h-full"><RefreshCw className="animate-spin text-blue-600" size={24} /><span className="ml-2 text-gray-500">Cargando...</span></div>
        ) : tab === 'asme' ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-auto h-full">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
                  <SortHeader col="dn" label="DN" />
                  <SortHeader col="esp" label="Espesor (mm)" />
                  <th className="text-center px-3 py-2.5 font-semibold whitespace-nowrap">OD (mm)</th>
                  <SortHeader col="sch" label="SCH" />
                  <SortHeader col="std_xs" label="STD/XS" />
                  <SortHeader col="rel_kg_mt" label="Kg/Mt" align="right" />
                </tr>
              </thead>
              <tbody>
                {asmeFiltered.map((row, i) => (
                  <tr key={row.concatenar + '-' + i} className={`border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="text-center px-3 py-1.5 whitespace-nowrap">{row.dn}</td>
                    <td className="text-center px-3 py-1.5 whitespace-nowrap">{row.esp}</td>
                    <td className="text-center px-3 py-1.5 whitespace-nowrap text-gray-500">{row.od_mm || '-'}</td>
                    <td className="text-center px-3 py-1.5 whitespace-nowrap">{row.sch}</td>
                    <td className="text-center px-3 py-1.5 whitespace-nowrap text-gray-500">{row.std_xs === '. . .' ? '-' : row.std_xs}</td>
                    <td className="text-right px-3 py-1.5 whitespace-nowrap font-semibold tabular-nums">{row.rel_kg_mt != null ? fmtStock.format(row.rel_kg_mt) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-auto h-full">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
                  <SortHeader col="label" label="Label" align="left" />
                  <SortHeader col="weight_lb_ft" label="Weight (lb/ft)" align="right" />
                  <SortHeader col="od_mm" label="OD (mm)" align="right" />
                  <SortHeader col="mass_kg_m" label="Mass (kg/m)" align="right" />
                  {tab === 'csg' && <SortHeader col="wall_mm" label="Wall (mm)" align="right" />}
                </tr>
              </thead>
              <tbody>
                {filteredTbgCsg.map((row, i) => (
                  <tr key={row.label + '-' + i} className={`border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="text-left px-3 py-1.5 whitespace-nowrap font-medium">{row.label}</td>
                    <td className="text-right px-3 py-1.5 whitespace-nowrap tabular-nums">{row.weight_lb_ft != null ? fmt2dec.format(row.weight_lb_ft) : '-'}</td>
                    <td className="text-right px-3 py-1.5 whitespace-nowrap tabular-nums">{row.od_mm != null ? fmt2dec.format(row.od_mm) : '-'}</td>
                    <td className="text-right px-3 py-1.5 whitespace-nowrap tabular-nums font-semibold">{row.mass_kg_m != null ? fmt2dec.format(row.mass_kg_m) : '-'}</td>
                    {tab === 'csg' && <td className="text-right px-3 py-1.5 whitespace-nowrap tabular-nums">{row.wall_mm != null ? fmt2dec.format(row.wall_mm) : '-'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// PRESUPUESTOS
// ============================================

function ArticlePickerModal({ onSelect, onClose }) {
  const [tab, setTab] = useState('argentina'); // argentina | chile
  const [search, setSearch] = useState('');
  const [dataAR, setDataAR] = useState(null);
  const [dataCL, setDataCL] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ar, cl] = await Promise.all([
          api('/api/stock'),
          api('/api/stock-chile')
        ]);
        setDataAR((ar.data || []).filter(d => d.tiene_stock));
        setDataCL(cl.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const items = tab === 'argentina' ? (dataAR || []) : (dataCL || []);
  const filtered = useMemo(() => {
    if (!search.trim()) return items.slice(0, 100);
    const s = search.toLowerCase();
    return items.filter(r => {
      const desc = (r.articulo || r.descripcion || '').toLowerCase();
      const cod = (r.codigo || '').toLowerCase();
      return desc.includes(s) || cod.includes(s);
    }).slice(0, 100);
  }, [items, search]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Seleccionar Artículo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setTab('argentina')} className={`px-4 py-1.5 rounded text-sm font-medium ${tab === 'argentina' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Stock Argentina</button>
            <button onClick={() => setTab('chile')} className={`px-4 py-1.5 rounded text-sm font-medium ${tab === 'chile' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Zona Franca</button>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Buscar por código o descripción..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" autoFocus />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? <div className="p-8 text-center text-gray-400">Cargando...</div> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Código</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Descripción</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Stock</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">UM</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Precio</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-600">Moneda</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const stock = tab === 'argentina' ? (r.en_stock || 0) : (r.cantidad || 0);
                  const precio = tab === 'argentina' ? (r.precio_lista || 0) : 0;
                  const moneda = tab === 'argentina' ? (r.moneda || 'ARS') : 'USD';
                  const um = tab === 'argentina' ? (r.um || r.um_venta || '') : (r.um || '');
                  return (
                    <tr key={i} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => {
                      onSelect({
                        origen: tab, codigo: r.codigo || '',
                        descripcion: r.articulo || r.descripcion || '',
                        um, precio_unitario: precio, moneda, cantidad: 1, descuento: 0
                      });
                    }}>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{r.codigo}</td>
                      <td className="px-3 py-2 text-gray-800">{r.articulo || r.descripcion}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtStock.format(stock)}</td>
                      <td className="px-3 py-2 text-center text-gray-500">{um}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{precio > 0 ? formatCurrency(precio, moneda) : '-'}</td>
                      <td className="px-3 py-2 text-center text-gray-500">{moneda}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Sin resultados</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-3 border-t text-xs text-gray-400 text-right">
          Mostrando {filtered.length} artículos (máx 100). {tab === 'chile' && 'Precios no disponibles desde Chile - ingrese manualmente.'}
        </div>
      </div>
    </div>
  );
}

function printPresupuesto(p) {
  const w = window.open('', '_blank', 'width=800,height=600');
  if (!w) return;
  const itemsHTML = (p.items || []).map((it, i) => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center">${i + 1}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee">${it.origen === 'chile' ? '🇨🇱' : '🇦🇷'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;font-family:monospace;font-size:11px">${it.codigo}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee">${it.descripcion}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${it.cantidad}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:center">${it.um}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${it.moneda} ${it.precio_unitario.toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right">${it.descuento}%</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:500">${it.moneda} ${it.subtotal.toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
    </tr>
  `).join('');

  w.document.write(`<!DOCTYPE html><html><head><title>Presupuesto ${p.numero}</title>
    <style>body{font-family:Arial,sans-serif;margin:30px;color:#333}table{width:100%;border-collapse:collapse}
    th{background:#004a8a;color:#fff;padding:8px;text-align:left;font-size:13px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px}
    .info{font-size:13px;line-height:1.6}.total{font-size:16px;font-weight:bold;margin-top:15px}
    @media print{body{margin:15px}}</style></head><body>
    <div class="header">
      <div><h2 style="color:#004a8a;margin:0">CUENCA DEL SUR</h2><p style="color:#666;margin:4px 0">Presupuesto Comercial</p></div>
      <div style="text-align:right"><h3 style="color:#004a8a;margin:0">${p.numero}</h3>
        <p style="color:#666;margin:4px 0">${new Date(p.fecha).toLocaleDateString('es-AR')}</p>
        <span style="padding:3px 10px;border-radius:12px;font-size:11px;background:${p.estado === 'aprobado' ? '#dcfce7' : p.estado === 'enviado' ? '#dbeafe' : p.estado === 'rechazado' ? '#fee2e2' : '#f3f4f6'};color:${p.estado === 'aprobado' ? '#166534' : p.estado === 'enviado' ? '#1e40af' : p.estado === 'rechazado' ? '#991b1b' : '#374151'}">${p.estado.toUpperCase()}</span>
      </div>
    </div>
    <div class="info" style="display:grid;grid-template-columns:1fr 1fr;gap:8px 30px;margin-bottom:20px;padding:12px;background:#f8fafc;border-radius:8px">
      <div><strong>Cliente:</strong> ${p.cliente}</div>
      <div><strong>Vendedor:</strong> ${p.vendedor || '-'}</div>
      <div><strong>Condición de pago:</strong> ${p.condicion_pago || '-'}</div>
      <div><strong>Validez:</strong> ${p.validez_dias} días</div>
      ${p.observaciones ? `<div style="grid-column:1/-1"><strong>Observaciones:</strong> ${p.observaciones}</div>` : ''}
    </div>
    <table><thead><tr><th>#</th><th></th><th>Código</th><th>Descripción</th><th style="text-align:right">Cant.</th><th style="text-align:center">UM</th><th style="text-align:right">P.Unit.</th><th style="text-align:right">Desc.</th><th style="text-align:right">Subtotal</th></tr></thead>
    <tbody>${itemsHTML}</tbody></table>
    <div style="text-align:right;margin-top:15px">
      ${p.total_usd > 0 ? `<div class="total">Total USD: USD ${p.total_usd.toLocaleString('es-AR', {minimumFractionDigits:2})}</div>` : ''}
      ${p.total_ars > 0 ? `<div class="total">Total ARS: $${Math.round(p.total_ars).toLocaleString('es-AR')}</div>` : ''}
    </div>
    <script>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}

function PresupuestosPage() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit
  const [current, setCurrent] = useState(null); // presupuesto en edición
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLista = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api('/api/presupuestos');
      setLista(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadLista(); }, [loadLista]);

  const nuevoPresupuesto = () => {
    setCurrent({
      cliente: '', vendedor: '', validez_dias: 15, condicion_pago: '',
      observaciones: '', items: [], estado: 'borrador'
    });
    setView('edit');
  };

  const editarPresupuesto = async (id) => {
    try {
      const res = await api(`/api/presupuestos/${id}`);
      setCurrent(res.data);
      setView('edit');
    } catch (e) { console.error(e); }
  };

  const guardar = async () => {
    if (!current.cliente?.trim()) return alert('Ingrese el nombre del cliente');
    setSaving(true);
    try {
      if (current.id) {
        await api(`/api/presupuestos/${current.id}`, { method: 'PUT', body: JSON.stringify(current) });
      } else {
        await api('/api/presupuestos', { method: 'POST', body: JSON.stringify(current) });
      }
      setView('list');
      setCurrent(null);
      loadLista();
    } catch (e) { alert('Error guardando: ' + e.message); }
    setSaving(false);
  };

  const duplicar = async (id) => {
    try {
      await api(`/api/presupuestos/${id}/duplicar`, { method: 'POST' });
      loadLista();
    } catch (e) { console.error(e); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    try {
      await api(`/api/presupuestos/${id}`, { method: 'DELETE' });
      loadLista();
    } catch (e) { console.error(e); }
  };

  const imprimirFromList = async (id) => {
    try {
      const res = await api(`/api/presupuestos/${id}`);
      printPresupuesto(res.data);
    } catch (e) { console.error(e); }
  };

  const addArticle = (art) => {
    setCurrent(prev => ({
      ...prev,
      items: [...prev.items, { ...art, id: Date.now() }]
    }));
    setShowPicker(false);
  };

  const removeItem = (idx) => {
    setCurrent(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const updateItem = (idx, field, value) => {
    setCurrent(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const totals = useMemo(() => {
    if (!current?.items) return { usd: 0, ars: 0 };
    let usd = 0, ars = 0;
    for (const it of current.items) {
      const sub = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) * (1 - (Number(it.descuento) || 0) / 100);
      if (it.moneda === 'USD') usd += sub; else ars += sub;
    }
    return { usd: Math.round(usd * 100) / 100, ars: Math.round(ars * 100) / 100 };
  }, [current?.items]);

  const estadoBadge = (estado) => {
    const colors = {
      borrador: 'bg-gray-100 text-gray-700',
      enviado: 'bg-blue-100 text-blue-700',
      aprobado: 'bg-green-100 text-green-700',
      rechazado: 'bg-red-100 text-red-700'
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[estado] || colors.borrador}`}>{estado}</span>;
  };

  // ---- VISTA EDICION ----
  if (view === 'edit' && current) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">{current.id ? `Editar ${current.numero}` : 'Nuevo Presupuesto'}</h2>
          <div className="flex gap-2">
            {current.id && (
              <button onClick={() => printPresupuesto(current)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                <Printer size={14} /> Imprimir
              </button>
            )}
            <button onClick={() => { setView('list'); setCurrent(null); }} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        {/* Cabecera */}
        <div className="bg-white rounded-lg border p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cliente *</label>
              <input type="text" value={current.cliente} onChange={e => setCurrent(p => ({ ...p, cliente: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="Nombre del cliente" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vendedor</label>
              <input type="text" value={current.vendedor} onChange={e => setCurrent(p => ({ ...p, vendedor: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="Nombre del vendedor" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Condición de Pago</label>
              <input type="text" value={current.condicion_pago} onChange={e => setCurrent(p => ({ ...p, condicion_pago: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="Ej: 30 días" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Validez (días)</label>
              <input type="number" value={current.validez_dias} onChange={e => setCurrent(p => ({ ...p, validez_dias: parseInt(e.target.value) || 15 }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
              <select value={current.estado} onChange={e => setCurrent(p => ({ ...p, estado: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                <option value="borrador">Borrador</option>
                <option value="enviado">Enviado</option>
                <option value="aprobado">Aprobado</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Observaciones</label>
              <textarea value={current.observaciones} onChange={e => setCurrent(p => ({ ...p, observaciones: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" rows={2} placeholder="Observaciones adicionales..." />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-lg border">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-medium text-gray-800">Artículos ({current.items.length})</h3>
            <button onClick={() => setShowPicker(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
              <Plus size={14} /> Agregar artículo
            </button>
          </div>
          {current.items.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <ClipboardList size={32} className="mx-auto mb-2 opacity-50" />
              <p>No hay artículos. Haga click en "Agregar artículo" para comenzar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#004a8a] text-white">
                    <th className="px-2 py-2 text-center w-8">#</th>
                    <th className="px-2 py-2 text-center w-8"></th>
                    <th className="px-2 py-2 text-left">Código</th>
                    <th className="px-2 py-2 text-left">Descripción</th>
                    <th className="px-2 py-2 text-right w-20">Cant.</th>
                    <th className="px-2 py-2 text-center w-16">UM</th>
                    <th className="px-2 py-2 text-right w-28">P.Unit.</th>
                    <th className="px-2 py-2 text-center w-16">Mon.</th>
                    <th className="px-2 py-2 text-right w-16">Desc.%</th>
                    <th className="px-2 py-2 text-right w-28">Subtotal</th>
                    <th className="px-2 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {current.items.map((it, i) => {
                    const sub = (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0) * (1 - (Number(it.descuento) || 0) / 100);
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-2 py-2 text-center text-gray-400">{i + 1}</td>
                        <td className="px-2 py-2 text-center">{it.origen === 'chile' ? '🇨🇱' : '🇦🇷'}</td>
                        <td className="px-2 py-2 font-mono text-xs text-gray-600">{it.codigo}</td>
                        <td className="px-2 py-2 text-gray-800 truncate max-w-xs" title={it.descripcion}>{it.descripcion}</td>
                        <td className="px-2 py-2">
                          <input type="number" value={it.cantidad} onChange={e => updateItem(i, 'cantidad', parseFloat(e.target.value) || 0)}
                            className="w-full text-right px-1 py-0.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-300" min="0" step="any" />
                        </td>
                        <td className="px-2 py-2 text-center text-gray-500 text-xs">{it.um}</td>
                        <td className="px-2 py-2">
                          <input type="number" value={it.precio_unitario} onChange={e => updateItem(i, 'precio_unitario', parseFloat(e.target.value) || 0)}
                            className="w-full text-right px-1 py-0.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-300" min="0" step="0.01" />
                        </td>
                        <td className="px-2 py-2">
                          <select value={it.moneda} onChange={e => updateItem(i, 'moneda', e.target.value)}
                            className="w-full px-1 py-0.5 border rounded text-sm text-center bg-white focus:outline-none focus:ring-1 focus:ring-blue-300">
                            <option value="USD">USD</option>
                            <option value="ARS">ARS</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={it.descuento} onChange={e => updateItem(i, 'descuento', parseFloat(e.target.value) || 0)}
                            className="w-full text-right px-1 py-0.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-300" min="0" max="100" step="0.1" />
                        </td>
                        <td className="px-2 py-2 text-right font-medium tabular-nums">{formatCurrency(Math.round(sub * 100) / 100, it.moneda)}</td>
                        <td className="px-2 py-2 text-center">
                          <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Totales */}
          {current.items.length > 0 && (
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-6">
              {totals.usd > 0 && <div className="text-right"><span className="text-xs text-gray-500">Total USD</span><div className="text-lg font-bold text-gray-800">USD {totals.usd.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div></div>}
              {totals.ars > 0 && <div className="text-right"><span className="text-xs text-gray-500">Total ARS</span><div className="text-lg font-bold text-gray-800">${Math.round(totals.ars).toLocaleString('es-AR')}</div></div>}
              {totals.usd === 0 && totals.ars === 0 && <div className="text-gray-400 text-sm">Ingrese cantidades y precios</div>}
            </div>
          )}
        </div>

        {showPicker && <ArticlePickerModal onSelect={addArticle} onClose={() => setShowPicker(false)} />}
      </div>
    );
  }

  // ---- VISTA LISTA ----
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Presupuestos</h2>
        <button onClick={nuevoPresupuesto} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm">
          <Plus size={16} /> Nuevo Presupuesto
        </button>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Cargando...</div>
        ) : lista.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-400">No hay presupuestos creados</p>
            <button onClick={nuevoPresupuesto} className="mt-3 text-sm text-blue-600 hover:text-blue-800">Crear el primero</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#004a8a] text-white">
                <th className="px-3 py-2.5 text-left font-medium">Número</th>
                <th className="px-3 py-2.5 text-center font-medium">Estado</th>
                <th className="px-3 py-2.5 text-left font-medium">Cliente</th>
                <th className="px-3 py-2.5 text-left font-medium">Vendedor</th>
                <th className="px-3 py-2.5 text-left font-medium">Fecha</th>
                <th className="px-3 py-2.5 text-right font-medium">Items</th>
                <th className="px-3 py-2.5 text-right font-medium">Total USD</th>
                <th className="px-3 py-2.5 text-right font-medium">Total ARS</th>
                <th className="px-3 py-2.5 text-center font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(p => (
                <tr key={p.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-blue-700">{p.numero}</td>
                  <td className="px-3 py-2.5 text-center">{estadoBadge(p.estado)}</td>
                  <td className="px-3 py-2.5 text-gray-800">{p.cliente}</td>
                  <td className="px-3 py-2.5 text-gray-600">{p.vendedor || '-'}</td>
                  <td className="px-3 py-2.5 text-gray-500">{new Date(p.fecha).toLocaleDateString('es-AR')}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500">{p.items_count}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{p.total_usd > 0 ? `USD ${p.total_usd.toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '-'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-medium">{p.total_ars > 0 ? `$${Math.round(p.total_ars).toLocaleString('es-AR')}` : '-'}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => editarPresupuesto(p.id)} title="Editar" className="p-1 text-gray-400 hover:text-blue-600"><Edit3 size={15} /></button>
                      <button onClick={() => duplicar(p.id)} title="Duplicar" className="p-1 text-gray-400 hover:text-green-600"><Copy size={15} /></button>
                      <button onClick={() => imprimirFromList(p.id)} title="Imprimir" className="p-1 text-gray-400 hover:text-gray-700"><Printer size={15} /></button>
                      <button onClick={() => eliminar(p.id)} title="Eliminar" className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================

// ============================================
// CONFIGURACIÓN (Reglas de Norma)
// ============================================
function ConfiguracionPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const NORMA_OPTIONS = [
    { label: 'ASME B36.10M', value: 'asme' },
    { label: 'API 5CT TBG', value: 'tbg' },
    { label: 'API 5CT CSG', value: 'csg' },
  ];

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const d = await api('/api/norma-config');
      setConfig(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const addRule = () => {
    setConfig(prev => ({
      ...prev,
      reglas: [...prev.reglas, { tipo: '', condicion: '', industria: '', norma: 'asme' }]
    }));
  };

  const removeRule = (idx) => {
    setConfig(prev => ({ ...prev, reglas: prev.reglas.filter((_, i) => i !== idx) }));
  };

  const updateRule = (idx, field, value) => {
    setConfig(prev => {
      const reglas = [...prev.reglas];
      reglas[idx] = { ...reglas[idx], [field]: value };
      return { ...prev, reglas };
    });
  };

  const updateDefault = (value) => {
    setConfig(prev => ({ ...prev, norma_default: value || null }));
  };

  const handleSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      await api('/api/norma-config', { method: 'PUT', body: JSON.stringify(config) });
      setSuccess('Configuración guardada correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><RefreshCw className="animate-spin text-blue-600" size={24} /><span className="ml-2 text-gray-500">Cargando configuración...</span></div>;

  return (
    <div className="h-[calc(100vh-56px)] overflow-auto p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 shadow-md">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-700">Reglas de asignación de norma</h3>
            <div className="flex items-center gap-2">
              {success && <span className="text-xs text-emerald-600 font-medium">{success}</span>}
              {error && <span className="text-xs text-red-500">{error}</span>}
              <button onClick={addRule} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-300 rounded hover:bg-blue-100 transition-colors">
                <Plus size={12} /> Agregar regla
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 px-4 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors disabled:opacity-50">
                {saving ? <RefreshCw size={12} className="animate-spin" /> : null} Guardar
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2 text-center w-10">#</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Condición</th>
                <th className="px-3 py-2 text-left">Industria</th>
                <th className="px-3 py-2 text-left">Norma</th>
                <th className="px-3 py-2 text-center w-12"></th>
              </tr>
            </thead>
            <tbody>
              {(config?.reglas || []).map((rule, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 text-center text-gray-400 text-xs">{i + 1}</td>
                  <td className="px-3 py-2">
                    <select value={rule.tipo} onChange={e => updateRule(i, 'tipo', e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                      <option value="">-- Tipo --</option>
                      {(config.opciones?.tipos || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select value={rule.condicion} onChange={e => updateRule(i, 'condicion', e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                      <option value="">-- Condición --</option>
                      {(config.opciones?.condiciones || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select value={rule.industria || ''} onChange={e => updateRule(i, 'industria', e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                      <option value="">-- Cualquiera --</option>
                      {(config.opciones?.industrias || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select value={rule.norma || ''} onChange={e => updateRule(i, 'norma', e.target.value)}
                      className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                      {NORMA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-300 bg-gray-50">
                <td className="px-3 py-2 text-center text-gray-400 text-xs">*</td>
                <td colSpan="3" className="px-3 py-2 text-xs text-gray-500 italic">Norma por defecto (para artículos que no matchean ninguna regla)</td>
                <td className="px-3 py-2">
                  <select value={config?.norma_default || ''} onChange={e => updateDefault(e.target.value)}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">Sin norma</option>
                    {NORMA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// VENTAS
// ============================================
function VentasPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedVendedor, setExpandedVendedor] = useState(new Set());
  const [expandedRubroCat, setExpandedRubroCat] = useState(new Set());

  const RUBRO_DIMS = [
    { key: 'condicion', label: 'Condición' },
    { key: 'industria', label: 'Industria' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'categoria', label: 'Categoría' },
  ];

  const loadData = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const d = await api('/api/ventas');
      setData(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading && !data) return <div className="flex items-center justify-center h-full"><RefreshCw className="animate-spin text-blue-600" size={24} /><span className="ml-2 text-gray-500">Cargando ventas...</span></div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return null;

  const toggleVendedor = (v) => {
    setExpandedVendedor(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });
  };
  const toggleRubroCat = (key) => {
    setExpandedRubroCat(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const kpi = data.kpi || {};
  const maxMesActual = Math.max(...(data.vendedores || []).map(v => v.mesActual || 0), 1);
  const deltaMesAnterior = kpi.mesAnterior > 0 ? Math.round(((kpi.mesActual - kpi.mesAnterior) / kpi.mesAnterior) * 100) : null;

  const kpis = [
    { label: 'Ventas Mes Actual', value: kpi.mesActual, delta: deltaMesAnterior, color: '#3b82f6', ops: kpi.opsMesActual, clts: kpi.clientesMesActual },
    { label: 'Ventas Trimestre', value: kpi.trimestre, color: '#8b5cf6' },
    { label: 'Ventas Año', value: kpi.anio, color: '#10b981' },
  ];

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col overflow-auto">
      <div className="p-4 space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          {kpis.map(k => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{k.label}</p>
              <p className="text-2xl font-bold" style={{ color: k.color }}>{formatCurrency(k.value, 'ARS')}</p>
              {k.delta != null && (
                <div className={`flex items-center gap-1 mt-1.5 text-xs ${k.delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {k.delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {k.delta >= 0 ? '+' : ''}{k.delta}% vs mes anterior
                </div>
              )}
              {(k.ops || k.clts) && (
                <div className="mt-1.5 pt-1.5 text-[11px] text-gray-500" style={{ borderTop: '1px solid #e5e7eb' }}>
                  {k.ops} ops · {k.clts} clientes
                </div>
              )}
            </div>
          ))}
        </div>

        {data.objetivo && kpi.mesActual > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Objetivo mensual</span>
              <span className="text-xs font-medium">{Math.round((kpi.mesActual / data.objetivo) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${Math.min((kpi.mesActual / data.objetivo) * 100, 100)}%` }} />
            </div>
          </div>
        )}

        {/* Vendedores Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-20">
              <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
                <th className="text-left px-3 py-2.5 font-semibold">Vendedor</th>
                <th className="text-right px-3 py-2.5 font-semibold">Mes Actual</th>
                <th className="text-center px-2 py-2.5 font-semibold">Ops</th>
                <th className="text-center px-2 py-2.5 font-semibold">Clts</th>
                <th className="text-right px-3 py-2.5 font-semibold">Mes Anterior</th>
                <th className="text-right px-3 py-2.5 font-semibold">Mismo Mes Año Ant</th>
                <th className="text-right px-3 py-2.5 font-semibold">Trimestre</th>
                <th className="text-right px-3 py-2.5 font-semibold">Año</th>
              </tr>
            </thead>
            <tbody>
              {(data.vendedores || []).map((v, i) => {
                const isExpanded = expandedVendedor.has(v.vendedor);
                return (
                  <React.Fragment key={v.vendedor}>
                    <tr className={`border-b border-gray-100 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : i % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'}`}
                      onClick={() => toggleVendedor(v.vendedor)}>
                      <td className="px-3 py-2 font-medium flex items-center gap-1">
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        {v.vendedor}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 bg-gray-100 rounded h-3 overflow-hidden">
                            <div className="h-full rounded" style={{ width: `${(v.mesActual / maxMesActual) * 100}%`, backgroundColor: 'rgba(59,130,246,0.3)' }} />
                          </div>
                          {formatCurrency(v.mesActual, 'ARS')}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center text-gray-500">{v.opsMesActual || '-'}</td>
                      <td className="px-2 py-2 text-center text-gray-500">{v.clientesMesActual || '-'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{formatCurrency(v.mesAnterior, 'ARS')}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{formatCurrency(v.mismoMesAnioAnt, 'ARS')}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{formatCurrency(v.trimestre, 'ARS')}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency(v.anio, 'ARS')}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="8" className="bg-blue-50/50 px-4 py-3">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            {RUBRO_DIMS.map(d => {
                              const entries = Object.entries(v.rubroMesActual?.[d.key] || {}).sort(([,a], [,b]) => b - a);
                              if (entries.length === 0) return null;
                              return (
                                <div key={d.key}>
                                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">{d.label}</p>
                                  <div className="space-y-0.5">
                                    {entries.map(([catName, catMonto]) => {
                                      const rubKey = `${v.vendedor}|${d.key}|${catName}`;
                                      const rubExpanded = expandedRubroCat.has(rubKey);
                                      const detalle = v.rubroDetalle?.[d.key]?.[catName] || [];
                                      return (
                                        <div key={catName}>
                                          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-100/50 cursor-pointer text-xs"
                                            onClick={(e) => { e.stopPropagation(); toggleRubroCat(rubKey); }}>
                                            {detalle.length > 0 && (rubExpanded ? <ChevronDown size={9} /> : <ChevronRight size={9} />)}
                                            <span className="font-medium text-gray-700 truncate">{catName || 'Sin clasificar'}</span>
                                            <span className="text-gray-400 ml-auto tabular-nums text-[11px]">{formatCurrency(catMonto, 'ARS')}</span>
                                          </div>
                                          {rubExpanded && detalle.length > 0 && (
                                            <div className="ml-4 mt-0.5 mb-1 space-y-0.5" style={{ borderLeft: '2px solid #818cf8' }}>
                                              {detalle.map((art, ai) => (
                                                <div key={ai} className="flex items-center gap-2 px-2 py-0.5 text-[11px] text-gray-500">
                                                  <span className="truncate flex-1">{art.desc || art.modelo}</span>
                                                  {art.ops > 0 && <span className="tabular-nums text-gray-400">{art.ops} ops</span>}
                                                  <span className="tabular-nums font-medium text-gray-600">{formatCurrency(art.monto, 'ARS')}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {data.vendedores?.length > 0 && (
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(kpi.mesActual, 'ARS')}</td>
                  <td className="px-2 py-2 text-center">{kpi.opsMesActual || ''}</td>
                  <td className="px-2 py-2 text-center">{kpi.clientesMesActual || ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(kpi.mesAnterior, 'ARS')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(kpi.mismoMesAnioAnt, 'ARS')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(kpi.trimestre, 'ARS')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(kpi.anio, 'ARS')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COTIZACIONES PROVEEDORES
// ============================================
function LPPickerModal({ onSelect, onClose }) {
  const [lps, setLps] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/lista-precios').then(d => { setLps(d.listasPrecios || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = lps.filter(lp => {
    if (!search) return true;
    const q = search.toLowerCase();
    return lp.nombre?.toLowerCase().includes(q) || String(lp.numero).includes(q);
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[500px] max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <span className="font-semibold text-sm">Seleccionar Lista de Precios</span>
          <div className="ml-auto relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none w-40" />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? <div className="p-4 text-center text-gray-400 text-sm">Cargando...</div> : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-gray-500 uppercase text-[10px]">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Nombre</th>
                  <th className="px-3 py-2 text-center">Origen</th>
                  <th className="px-3 py-2 text-right">P$ Lista</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(lp => (
                  <tr key={lp.numero} onClick={() => onSelect(lp)} className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors">
                    <td className="px-3 py-1.5 font-mono">{lp.numero}</td>
                    <td className="px-3 py-1.5">{lp.nombre}</td>
                    <td className="px-3 py-1.5 text-center">{lp.origen || '-'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{lp.precio_lista != null ? formatCurrency(lp.precio_lista, lp.moneda) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ComparacionBadge({ precioProveedor, precioNuestro, monedaProv, monedaNuestra }) {
  if (!precioProveedor || !precioNuestro) return null;
  if (monedaProv !== monedaNuestra) return <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">~</span>;
  const diff = ((precioProveedor - precioNuestro) / precioNuestro) * 100;
  if (diff > 0) return <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">+{diff.toFixed(1)}%</span>;
  return <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">{diff.toFixed(1)}%</span>;
}

function CotizacionesProveedoresPage() {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [lpPickerIdx, setLpPickerIdx] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [diccionario, setDiccionario] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const [cots, dic] = await Promise.all([
        api('/api/cotizaciones-prov'),
        api('/api/diccionario-proveedores').catch(() => ({}))
      ]);
      setCotizaciones(cots.data || cots);
      setDiccionario(dic);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const startEdit = async (cot) => {
    setEditingId(cot.id);
    setForm({ ...cot, items: cot.items ? [...cot.items.map(it => ({ ...it }))] : [] });
    if (cot.tiene_pdf) {
      try {
        const res = await fetch(`${API_URL}/api/cotizaciones-prov/${cot.id}/pdf`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) { const blob = await res.blob(); setPdfUrl(URL.createObjectURL(blob)); }
      } catch (e) { /* no pdf */ }
    } else { setPdfUrl(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cotización?')) return;
    try {
      await api(`/api/cotizaciones-prov/${id}`, { method: 'DELETE' });
      loadData();
    } catch (e) { setError(e.message); }
  };

  const handleSave = async () => {
    try {
      setError('');
      await api(`/api/cotizaciones-prov/${form.id}`, { method: 'PUT', body: JSON.stringify(form) });
      // Save diccionario entries
      const newEntries = {};
      form.items.forEach(it => {
        if (it.descripcion_proveedor && it.lp_numero) {
          const key = it.descripcion_proveedor.trim().toLowerCase();
          if (key) newEntries[key] = it.lp_numero;
        }
      });
      if (Object.keys(newEntries).length > 0) {
        await api('/api/diccionario-proveedores', { method: 'POST', body: JSON.stringify(newEntries) }).catch(() => {});
      }
      setEditingId(null); setForm(null); setPdfUrl(null);
      loadData();
    } catch (e) { setError(e.message); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      await fetch(`${API_URL}/api/cotizaciones-prov/${form.id}/pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      const blob = new Blob([file], { type: 'application/pdf' });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) { setError(e.message); }
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { descripcion_proveedor: '', cantidad: 1, um: 'Unidad', precio_proveedor: 0, lp_numero: null, lp_nombre: '' }]
    }));
  };

  const removeItem = (idx) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleDescBlur = (idx) => {
    const desc = form.items[idx]?.descripcion_proveedor?.trim().toLowerCase();
    if (desc && diccionario[desc] && !form.items[idx].lp_numero) {
      updateItem(idx, 'lp_numero', diccionario[desc]);
    }
  };

  const handleLpSelect = (lp) => {
    if (lpPickerIdx !== null) {
      updateItem(lpPickerIdx, 'lp_numero', lp.numero);
      updateItem(lpPickerIdx, 'lp_nombre', lp.nombre);
      updateItem(lpPickerIdx, 'precio_nuestro', lp.precio_lista);
      updateItem(lpPickerIdx, 'moneda_nuestra', lp.moneda);
    }
    setLpPickerIdx(null);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><RefreshCw className="animate-spin text-blue-600" size={24} /><span className="ml-2 text-gray-500">Cargando cotizaciones...</span></div>;

  // Edit view
  if (editingId && form) {
    return (
      <div className="h-[calc(100vh-56px)] flex">
        {lpPickerIdx !== null && <LPPickerModal onSelect={handleLpSelect} onClose={() => setLpPickerIdx(null)} />}
        {/* PDF viewer */}
        <div className="w-[40%] border-r border-gray-200 bg-gray-100 flex flex-col">
          <div className="px-3 py-2 bg-white border-b border-gray-200 flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600">PDF</span>
            <label className="ml-auto flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-300 rounded cursor-pointer hover:bg-blue-100">
              <Upload size={10} /> Subir PDF
              <input type="file" accept=".pdf" onChange={handlePdfUpload} className="hidden" />
            </label>
          </div>
          <div className="flex-1">
            {pdfUrl ? (
              <object data={pdfUrl} type="application/pdf" className="w-full h-full">PDF no disponible</object>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Sin PDF</div>
            )}
          </div>
        </div>
        {/* Form + Items */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {error && <div className="bg-red-50 text-red-500 p-2 rounded text-xs border border-red-200">{error}</div>}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 uppercase">Proveedor</label>
              <input type="text" value={form.proveedor || ''} onChange={e => setForm(p => ({ ...p, proveedor: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-0.5" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase">Fecha</label>
              <input type="date" value={form.fecha || ''} onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-0.5" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase">Moneda</label>
              <select value={form.moneda || 'ARS'} onChange={e => setForm(p => ({ ...p, moneda: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-0.5">
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase">Condición de pago</label>
              <input type="text" value={form.condicion_pago || ''} onChange={e => setForm(p => ({ ...p, condicion_pago: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-0.5" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-gray-500 uppercase">Observaciones</label>
              <input type="text" value={form.observaciones || ''} onChange={e => setForm(p => ({ ...p, observaciones: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-0.5" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-600">Items</span>
              <button onClick={addItem} className="flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-300 rounded hover:bg-blue-100">
                <Plus size={10} /> Agregar
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                  <th className="px-2 py-1.5 text-center w-8">#</th>
                  <th className="px-2 py-1.5 text-left">Descripción Prov.</th>
                  <th className="px-2 py-1.5 text-center w-14">Cant</th>
                  <th className="px-2 py-1.5 text-center w-16">UM</th>
                  <th className="px-2 py-1.5 text-right w-20">P. Prov</th>
                  <th className="px-2 py-1.5 text-left w-28">LP Asignada</th>
                  <th className="px-2 py-1.5 text-right w-20">P. Nuestro</th>
                  <th className="px-2 py-1.5 text-center w-14">Dif%</th>
                  <th className="px-2 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-center text-gray-400">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={it.descripcion_proveedor || ''} onChange={e => updateItem(i, 'descripcion_proveedor', e.target.value)}
                        onBlur={() => handleDescBlur(i)} className="w-full text-xs border border-gray-200 rounded px-1.5 py-1" />
                    </td>
                    <td className="px-2 py-1.5"><input type="number" value={it.cantidad || ''} onChange={e => updateItem(i, 'cantidad', parseFloat(e.target.value) || 0)}
                      className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 text-center" /></td>
                    <td className="px-2 py-1.5"><input type="text" value={it.um || ''} onChange={e => updateItem(i, 'um', e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 text-center" /></td>
                    <td className="px-2 py-1.5"><input type="number" value={it.precio_proveedor || ''} onChange={e => updateItem(i, 'precio_proveedor', parseFloat(e.target.value) || 0)}
                      className="w-full text-xs border border-gray-200 rounded px-1.5 py-1 text-right" /></td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => setLpPickerIdx(i)}
                        className="text-[11px] text-blue-600 hover:text-blue-800 truncate block w-full text-left">
                        {it.lp_nombre || it.lp_numero || 'Asignar...'}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{it.precio_nuestro ? formatCurrency(it.precio_nuestro, it.moneda_nuestra || form.moneda) : '-'}</td>
                    <td className="px-2 py-1.5 text-center">
                      <ComparacionBadge precioProveedor={it.precio_proveedor} precioNuestro={it.precio_nuestro} monedaProv={form.moneda} monedaNuestra={it.moneda_nuestra || form.moneda} />
                    </td>
                    <td className="px-2 py-1.5 text-center"><button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleSave} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors">Guardar</button>
            <button onClick={() => { setEditingId(null); setForm(null); setPdfUrl(null); }} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors">Cancelar</button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="h-[calc(100vh-56px)] overflow-auto p-4">
      {error && <div className="bg-red-50 text-red-500 p-2 rounded text-xs border border-red-200 mb-3">{error}</div>}
      <div className="bg-white rounded-xl border border-gray-200 shadow-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20">
            <tr className="bg-[#004a8a] text-white uppercase text-xs tracking-wide">
              <th className="text-left px-3 py-2.5 font-semibold">Número</th>
              <th className="text-left px-3 py-2.5 font-semibold">Proveedor</th>
              <th className="text-center px-3 py-2.5 font-semibold">Fecha</th>
              <th className="text-center px-3 py-2.5 font-semibold">Moneda</th>
              <th className="text-center px-3 py-2.5 font-semibold">Items</th>
              <th className="text-left px-3 py-2.5 font-semibold">Cond. Pago</th>
              <th className="text-center px-3 py-2.5 font-semibold">PDF</th>
              <th className="text-center px-3 py-2.5 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cotizaciones.map((cot, i) => (
              <tr key={cot.id} className={`border-b border-gray-100 hover:bg-blue-50/40 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                <td className="px-3 py-2 font-mono text-xs">{cot.numero || cot.id}</td>
                <td className="px-3 py-2 font-medium">{cot.proveedor}</td>
                <td className="px-3 py-2 text-center text-gray-500">{cot.fecha}</td>
                <td className="px-3 py-2 text-center">{cot.moneda}</td>
                <td className="px-3 py-2 text-center">{cot.items?.length || 0}</td>
                <td className="px-3 py-2 text-gray-500">{cot.condicion_pago || '-'}</td>
                <td className="px-3 py-2 text-center">{cot.tiene_pdf ? <FileText size={14} className="inline text-blue-500" /> : '-'}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => startEdit(cot)} className="text-blue-500 hover:text-blue-700 p-1"><Edit3 size={14} /></button>
                    <button onClick={() => handleDelete(cot.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================
// OBSERVACIONES (Alertas + Checklist)
// ============================================
function ObservacionesPage() {
  const [tab, setTab] = useState('alertas');
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAlert, setExpandedAlert] = useState(new Set());

  const ALERT_RULES = [
    { id: 'condicion_otros', label: 'Condición "Otros"', severity: 'warning', test: item => item.condicion === 'Otros' },
    { id: 'sin_lp', label: 'Sin Lista de Precios', severity: 'error', test: item => item.lp_numero == null && item.stock_neto_np > 0 },
    { id: 'industria_otros', label: 'Industria "Otros"', severity: 'info', test: item => item.industria === 'Otros' },
    { id: 'stock_negativo', label: 'Stock neto negativo', severity: 'error', test: item => item.stock_neto_np < 0 },
    { id: 'sin_origen', label: 'Sin origen', severity: 'info', test: item => item.lp_numero != null && !item.origen },
  ];

  useEffect(() => {
    api('/api/stock').then(d => { setStockData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const alerts = useMemo(() => {
    if (!stockData?.data) return [];
    return ALERT_RULES.map(rule => {
      const items = stockData.data.filter(rule.test);
      return { ...rule, count: items.length, items: items.slice(0, 50) };
    }).filter(a => a.count > 0);
  }, [stockData]);

  const toggleAlert = (id) => {
    setExpandedAlert(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const sevColors = {
    error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', badge: 'bg-red-100 text-red-700' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
  };

  const totalAlerts = alerts.reduce((s, a) => s + a.count, 0);
  const criticalCount = alerts.filter(a => a.severity === 'error').reduce((s, a) => s + a.count, 0);
  const warningCount = alerts.filter(a => a.severity === 'warning').reduce((s, a) => s + a.count, 0);

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setTab('alertas')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${tab === 'alertas' ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
            Alertas {totalAlerts > 0 && <span className="ml-1 bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-[10px]">{totalAlerts}</span>}
          </button>
          <button onClick={() => setTab('checklist')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${tab === 'checklist' ? 'bg-white shadow-sm text-gray-800 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
            Checklist
          </button>
        </div>
        {tab === 'alertas' && !loading && (
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{totalAlerts} alertas</span>
            {criticalCount > 0 && <span className="text-red-500 font-medium">{criticalCount} críticas</span>}
            {warningCount > 0 && <span className="text-amber-500 font-medium">{warningCount} advertencias</span>}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {tab === 'alertas' ? (
          loading ? (
            <div className="flex items-center justify-center h-full"><RefreshCw className="animate-spin text-blue-600" size={24} /><span className="ml-2 text-gray-500">Analizando stock...</span></div>
          ) : alerts.length === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
              <div className="text-emerald-600 font-semibold text-lg">Sin alertas</div>
              <div className="text-emerald-500 text-sm mt-1">Todo está en orden</div>
            </div>
          ) : (
            <div className="space-y-3 max-w-4xl mx-auto">
              {alerts.map(alert => {
                const c = sevColors[alert.severity];
                const isOpen = expandedAlert.has(alert.id);
                return (
                  <div key={alert.id} className={`${c.bg} ${c.border} border rounded-xl overflow-hidden`}>
                    <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => toggleAlert(alert.id)}>
                      {isOpen ? <ChevronDown size={14} className={c.text} /> : <ChevronRight size={14} className={c.text} />}
                      <AlertTriangle size={14} className={c.text} />
                      <span className={`text-sm font-medium ${c.text}`}>{alert.label}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${c.badge}`}>{alert.count}</span>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[10px] text-gray-500 uppercase border-b">
                              <th className="py-1 text-left">Código</th>
                              <th className="py-1 text-left">Artículo</th>
                              <th className="py-1 text-right">Stock Neto</th>
                            </tr>
                          </thead>
                          <tbody>
                            {alert.items.map((it, i) => (
                              <tr key={it.codigo + '-' + i} className="border-b border-gray-100/50">
                                <td className="py-1 font-mono text-[11px]">{it.codigo}</td>
                                <td className="py-1 truncate max-w-[300px]">{it.articulo}</td>
                                <td className="py-1 text-right tabular-nums">{fmtStock.format(it.stock_neto_np)}</td>
                              </tr>
                            ))}
                            {alert.count > 50 && <tr><td colSpan="3" className="py-1 text-gray-400 text-center">...y {alert.count - 50} más</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <ChecklistSection />
        )}
      </div>
    </div>
  );
}
// SIDEBAR + LAYOUT
// ============================================
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'stock', label: 'Stock', icon: Package },
  { id: 'stock-chile', label: 'Zona Franca', icon: Globe },
  { id: 'articulos', label: 'Artículos', icon: FileText },
  { id: 'presupuestos', label: 'Presupuestos', icon: ClipboardList },
  { id: 'ventas', label: 'Ventas', icon: TrendingUp },
];

const NAV_AJUSTES = [
  { id: 'lista-precios', label: 'Lista de Precios', icon: DollarSign },
  { id: 'normas', label: 'Normas', icon: BookOpen },
  { id: 'configuracion', label: 'Configuración', icon: Settings },
  { id: 'cotizaciones-prov', label: 'Cotiz. Proveedores', icon: ShoppingCart, badge: 'BETA' },
  { id: 'observaciones', label: 'Observaciones', icon: AlertTriangle },
];

function MainLayout({ onLogout }) {
  const [page, setPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await api('/api/refresh', { method: 'POST' });
      window.location.reload();
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const allNav = [...NAV_ITEMS, ...NAV_AJUSTES];
  const currentNav = allNav.find(n => n.id === page);

  return (
    <div className="h-screen flex overflow-hidden" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 text-white flex flex-col transition-all duration-200 ${sidebarCollapsed ? 'w-14' : 'w-56'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} style={{ background: 'linear-gradient(180deg, #004a8a 0%, #003d70 100%)' }}>
        <div className={`flex items-center ${sidebarCollapsed ? 'px-1 py-2 justify-center' : 'px-3 py-3'}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {sidebarCollapsed ? (
            <img src="/logo-cuenca.jpg" alt="Cuenca del Sur" className="w-10 h-auto rounded" />
          ) : (
            <img src="/logo-cuenca.jpg" alt="Cuenca del Sur" className="h-10 w-auto rounded" />
          )}
        </div>
        {/* Nav principal */}
        <nav className="flex-1 py-2">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = page === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setPage(item.id); setSidebarOpen(false); }}
                title={sidebarCollapsed ? item.label : undefined}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-2.5 text-sm transition-colors border-l-3 ${active ? 'font-medium border-l-2 border-white' : 'border-l-2 border-transparent'}`}
                style={active ? { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } : { color: 'rgba(255,255,255,0.8)' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!sidebarCollapsed && item.label}
              </button>
            );
          })}
        </nav>
        {/* Ajustes - pinned al fondo */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          {!sidebarCollapsed && <div className="px-4 pt-2 pb-1"><span className="text-[9px] uppercase tracking-widest font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>Ajustes</span></div>}
          {NAV_AJUSTES.map(item => {
            const Icon = item.icon;
            const active = page === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setPage(item.id); setSidebarOpen(false); }}
                title={sidebarCollapsed ? item.label : undefined}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-2.5 px-4'} py-1.5 text-xs transition-colors border-l-3 ${active ? 'font-medium border-l-2 border-white' : 'border-l-2 border-transparent'}`}
                style={active ? { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } : { color: 'rgba(255,255,255,0.55)' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon size={14} className="flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                {!sidebarCollapsed && item.badge && <span className="ml-auto text-[8px] bg-amber-400/80 text-amber-900 px-1 py-0.5 rounded font-bold">{item.badge}</span>}
              </button>
            );
          })}
        </div>
        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarCollapsed(c => !c)}
          className="hidden lg:flex items-center justify-center py-2 transition-colors"
          style={{ borderTop: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <ChevronLeft size={16} className={`transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
        </button>
        <div className={`py-3 ${sidebarCollapsed ? 'px-2' : 'px-4'}`} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={onLogout}
            title={sidebarCollapsed ? 'Cerrar sesión' : undefined}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2'} text-sm transition-colors`}
            style={{ color: 'rgba(255,255,255,0.7)' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
          >
            <LogOut size={16} className="flex-shrink-0" />
            {!sidebarCollapsed && 'Cerrar sesión'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1 hover:bg-gray-100 rounded text-gray-600">
            <Menu size={20} />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            {currentNav && <currentNav.icon size={20} style={{ color: '#006AB5' }} />}
            {currentNav?.label}
          </h2>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors text-gray-500"
              title="Refrescar datos"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto" style={{ backgroundColor: '#F5F7FA' }}>
          {page === 'dashboard' && <DashboardPage />}
          {page === 'stock' && <StockPage />}
          {page === 'stock-chile' && <StockChilePage />}
          {page === 'articulos' && <ArticulosPage />}
          {page === 'presupuestos' && <PresupuestosPage />}
          {page === 'ventas' && <VentasPage />}
          {page === 'lista-precios' && <ListaPreciosPage />}
          {page === 'normas' && <NormasPage />}
          {page === 'configuracion' && <ConfiguracionPage />}
          {page === 'cotizaciones-prov' && <CotizacionesProveedoresPage />}
          {page === 'observaciones' && <ObservacionesPage />}
        </main>
      </div>
    </div>
  );
}

// ============================================
// APP ROOT
// ============================================
export default function App() {
  const [user, setUser] = useState(localStorage.getItem('username'));

  const handleLogin = (username) => setUser(username);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api('/api/verify-token').catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        setUser(null);
      });
    }
  }, []);

  if (!user) return <LoginPage onLogin={handleLogin} />;
  return <MainLayout onLogout={handleLogout} />;
}
